/*
 * Goehner's Custom JS — centralized scripts for custom-* sections.
 * Loaded globally by layout/theme.liquid (see CLAUDE.md §5 Dawn exception list).
 * Per project naming convention: custom JS in a single assets/custom.js (D-7 spirit).
 *
 * Modules:
 *   1. Lazy video loading — IntersectionObserver-based, used by scene-wall + UGC carousel.
 *      Targets any element with [data-scene-tile] containing a <video data-src="...">.
 *      Plays when 30% visible, pauses when out of view. Idempotent on re-init.
 *   2. UGC carousel scroll — data-ugc-scroll="prev|next" buttons, used by custom-ugc-trending.
 *   3. Blog teaser carousel scroll — data-blog-scroll="prev|next" buttons, used by custom-blog-teaser.
 *   4. Carousel counter — [data-count-current] updater, used by UGC + Collection sections.
 *   5. PDP gallery — thumb swap + dot sync, used by custom-pdp-main.
 *      Scope-aware: all queries scoped to the [data-pdp-gallery] root.
 *      Idempotent via data-pdp-gallery-init guard.
 *   6. PDP variant select — radio swatch → hidden <select name="id"> sync,
 *      used by custom-pdp-main. Dispatches 'change' event for Shopify listeners.
 *      Idempotent via data-pdp-variants-init guard.
 *   7. PDP qty stepper — +/- buttons updating the number input, used by custom-pdp-main.
 *
 * Last updated: 2026-05-13
 */

(function () {
  'use strict';

  function getThemeRoute(routeName, fallbackPath) {
    if (window.routes && window.routes[routeName]) return window.routes[routeName];
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    root = String(root);
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + String(fallbackPath || '').replace(/^\//, '');
  }

  function getCartJsonUrl() {
    return getThemeRoute('cart_url', 'cart').replace(/\/$/, '') + '.js';
  }

  function updateCartBadges(cartState) {
    if (!cartState) return;
    document.querySelectorAll('.gh-nav__cart-count').forEach(function (badge) {
      badge.textContent = String(cartState.item_count);
      badge.classList.toggle('is-zero', cartState.item_count === 0);
    });
    var cartLink = document.querySelector('.gh-nav__cart-btn');
    if (cartLink) {
      var label = cartLink.getAttribute('aria-label') || '';
      if (/\d+/.test(label)) {
        cartLink.setAttribute('aria-label', label.replace(/\d+/, String(cartState.item_count)));
      }
    }
  }

  function fetchCartState() {
    return fetch(getCartJsonUrl(), { cache: 'no-store' }).then(function (response) {
      if (!response.ok) throw new Error('Cart refresh failed');
      return response.json();
    });
  }

  function getCartVariantQuantity(cartState, variantId) {
    return (cartState && cartState.items || []).reduce(function (total, line) {
      if (String(line.variant_id) !== String(variantId)) return total;
      var quantity = Number(line.quantity);
      return total + (Number.isFinite(quantity) ? quantity : 0);
    }, 0);
  }

  function getCartErrorMessage(payload) {
    if (!payload) return '';
    var message = payload.description || payload.message || payload.errors;
    if (typeof message === 'string') return message;
    if (Array.isArray(message)) {
      return message.filter(function (item) { return typeof item === 'string'; }).join(' ');
    }
    return '';
  }

  function getErrorFallback(errorEl, attributeName, error) {
    var message = error && error.message;
    if (message && message !== 'Failed to fetch') return message;
    return (errorEl && errorEl.getAttribute(attributeName)) || '';
  }

  function refreshCartTruth(cartUi, variantId, knownCartState) {
    var cartStatePromise = knownCartState
      ? Promise.resolve(knownCartState)
      : fetchCartState();
    return cartStatePromise.then(function (cartState) {
      updateCartBadges(cartState);
      if (!cartUi || typeof cartUi.getSectionsToRender !== 'function' ||
        typeof cartUi.renderContents !== 'function') return;

      var item = (cartState.items || []).find(function (line) {
        return String(line.variant_id) === String(variantId);
      });
      if (!item) return;

      var sectionIds = cartUi.getSectionsToRender().map(function (section) {
        return section.id;
      });
      if (!sectionIds.length) return;

      var sectionUrl = window.location.pathname +
        '?sections=' + encodeURIComponent(sectionIds.join(','));
      return fetch(sectionUrl, { cache: 'no-store' })
        .then(function (response) {
          if (!response.ok) throw new Error('Cart section refresh failed');
          return response.json();
        })
        .then(function (sections) {
          cartUi.renderContents({
            id: variantId,
            key: item.key,
            sections: sections
          });
        });
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Lazy video loading (scene wall + UGC carousel)
  // ─────────────────────────────────────────────────────────────
  // Two-observer design:
  //   loadIO  — rootMargin '800px 1200px', threshold 0
  //             Fires roughly one viewport ahead vertically (page scroll),
  //             with a wider horizontal buffer (UGC carousel swipe). Sets video.src + load()
  //             so buffering starts well before the user arrives.
  //             Unobserves after first trigger (load once, not repeatedly).
  //   playIO  — rootMargin '200px 0px', threshold 0.3
  //             Fires when tile is ~30 % visible. Plays or pauses only.
  //             If loadIO somehow hasn't fired yet (edge case), playIO
  //             also sets src to guarantee video plays.
  // ─────────────────────────────────────────────────────────────
  function initLazyVideos(root) {
    root = root || document;
    var tiles = root.querySelectorAll('[data-scene-tile]:not([data-scene-init])');
    if (!tiles.length) return;

    // Attach a one-time 'playing' listener to mark the tile as playing.
    // Guards against double-binding via dataset.playingListenerAttached.
    function attachPlayingListener(tile, video) {
      if (video.dataset.playingListenerAttached) return;
      video.dataset.playingListenerAttached = 'true';
      video.addEventListener('playing', function () {
        tile.classList.add('is-playing');
      });
    }

    // Attempt video.play() with graceful rejection handling.
    // On rejection: poster stays visible (is-playing NOT added).
    // Retries once after 600ms if the first attempt fails (covers TikTok
    // WebView first-load throttle). The retry flag prevents looping.
    function tryPlay(tile, video) {
      var p = video.play();
      if (p && p.then && p.catch) {
        p.catch(function () {
          if (video.dataset.playRetried) return;
          video.dataset.playRetried = 'true';
          setTimeout(function () {
            var p2 = video.play();
            if (p2 && p2.catch) p2.catch(function () {
              // Both attempts failed — poster remains visible, no further retry.
            });
          }, 600);
        });
      }
    }

    // Fallback for browsers without IntersectionObserver: load + play all immediately
    if (!('IntersectionObserver' in window)) {
      tiles.forEach(function (t) {
        t.setAttribute('data-scene-init', '');
        var v = t.querySelector('video');
        var src = v && v.dataset.src;
        if (src) {
          v.src = src;
          v.removeAttribute('data-src');
          v.preload = 'auto';
          v.load();
          attachPlayingListener(t, v);
          tryPlay(t, v);
        }
      });
      return;
    }

    // loadIO: wide margin, fires early, handles buffering only
    var loadIO = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var tile = entry.target;
          var video = tile.querySelector('video');
          if (!video || tile.dataset.loaded) return;
          var src = video.dataset.src;
          if (src) {
            video.src = src;
            video.removeAttribute('data-src');
            video.preload = 'auto';
            video.load();
          }
          tile.dataset.loaded = 'true';
          loadIO.unobserve(tile);
        });
      },
      { rootMargin: '800px 1200px', threshold: 0 }
    );

    // playIO: tighter margin, handles play/pause only
    var playIO = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var tile = entry.target;
          var video = tile.querySelector('video');
          if (!video) return;
          if (entry.isIntersecting) {
            // Safety net: if loadIO hasn't triggered yet (extreme edge case), load now
            if (!tile.dataset.loaded) {
              var src = video.dataset.src;
              if (src) {
                video.src = src;
                video.removeAttribute('data-src');
                video.preload = 'auto';
                video.load();
              }
              tile.dataset.loaded = 'true';
            }
            attachPlayingListener(tile, video);
            tryPlay(tile, video);
          } else {
            if (!video.paused) video.pause();
          }
        });
      },
      { rootMargin: '200px 0px', threshold: 0.3 }
    );

    tiles.forEach(function (t) {
      t.setAttribute('data-scene-init', '');
      var v = t.querySelector('video');
      if (v) attachPlayingListener(t, v);
      loadIO.observe(t);
      playIO.observe(t);
    });
  }

  // Defer the first below-fold Steamax image until it is actually visible or
  // the initial window load has completed, so it cannot compete with Hero LCP.
  function initSteamaxImages(root) {
    root = root || document;
    root.querySelectorAll('[data-steamax-bg]:not([data-steamax-init])').forEach(function (image) {
      image.setAttribute('data-steamax-init', '');
      var loaded = false;
      var observer;

      function loadImage() {
        if (loaded) return;
        loaded = true;
        if (observer) observer.disconnect();
        image.srcset = image.dataset.srcset;
        image.src = image.dataset.src;
        image.removeAttribute('data-srcset');
        image.removeAttribute('data-src');
      }

      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(function (entries) {
          if (!entries.some(function (entry) {
            return entry.isIntersecting && entry.intersectionRatio > 0;
          })) return;
          loadImage();
        }, { threshold: 0 });
        observer.observe(image);
      }

      if (document.readyState === 'complete') loadImage();
      else window.addEventListener('load', loadImage, { once: true });
    });
  }

  // ─────────────────────────────────────────────────────────────
  // UGC carousel scroll (data-ugc-scroll)
  // Used by: custom-ugc-trending.liquid
  // Arrow buttons carry data-ugc-scroll="prev"|"next".
  // Finds the nearest .gh-ugc__track sibling within .gh-ugc__wrap
  // so multiple section instances on one page are fully isolated.
  // ─────────────────────────────────────────────────────────────
  function initUgcScroll(root) {
    root = root || document;
    root.querySelectorAll('[data-ugc-scroll]:not([data-ugc-scroll-init])').forEach(function (btn) {
      btn.setAttribute('data-ugc-scroll-init', '');
      btn.addEventListener('click', function () {
        var dir = btn.getAttribute('data-ugc-scroll') === 'next' ? 1 : -1;
        var wrap = btn.closest('.gh-ugc__wrap');
        var track = wrap && wrap.querySelector('.gh-ugc__track');
        if (!track) return;
        var card = track.querySelector('.gh-ugc__card');
        if (!card) return;
        // Read actual gap from computed style so the step matches whatever breakpoint is active
        // (V13: mobile gap = var(--s-3) = 8px, desktop gap = 10px).
        var gapPx = parseFloat(getComputedStyle(track).gap) || 8;
        var step = card.getBoundingClientRect().width + gapPx;
        track.scrollBy({ left: dir * step, behavior: 'smooth' });
      });
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Blog teaser carousel scroll (data-blog-scroll)
  // Used by: custom-blog-teaser.liquid
  // Arrow buttons carry data-blog-scroll="prev"|"next".
  // Finds the nearest .gh-blog-tease__track sibling within
  // .gh-blog-tease__wrap so multiple instances are fully isolated.
  // ─────────────────────────────────────────────────────────────
  function initBlogScroll(root) {
    root = root || document;
    root.querySelectorAll('[data-blog-scroll]:not([data-blog-scroll-init])').forEach(function (btn) {
      btn.setAttribute('data-blog-scroll-init', '');
      btn.addEventListener('click', function () {
        var dir = btn.getAttribute('data-blog-scroll') === 'next' ? 1 : -1;
        var wrap = btn.closest('.gh-blog-tease__wrap');
        var track = wrap && wrap.querySelector('.gh-blog-tease__track');
        if (!track) return;
        var card = track.querySelector('.gh-blog-card');
        if (!card) return;
        var gapPx = parseFloat(getComputedStyle(track).gap) || 12;
        var step = card.getBoundingClientRect().width + gapPx;
        track.scrollBy({ left: dir * step, behavior: 'smooth' });
      });
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Carousel counter ([data-count-current])
  // Used by: custom-ugc-trending.liquid (and future gh-collection)
  // Listens to scroll on each .gh-ugc__track / .gh-collection__cards,
  // finds the center-most card, updates [data-count-current] text.
  // Idempotent via data-counter-init guard.
  // ─────────────────────────────────────────────────────────────
  function initCarouselCounters(root) {
    root = root || document;
    root.querySelectorAll(
      '.gh-ugc__track:not([data-counter-init]), .gh-collection__cards:not([data-counter-init])'
    ).forEach(function (track) {
      track.setAttribute('data-counter-init', '');
      var section = track.closest('.gh-ugc, .gh-collection') || track.parentElement;
      if (!section) return;
      var counter = section.querySelector('[data-count-current]');
      if (!counter) return;
      var items = track.children;

      var update = function () {
        var r = track.getBoundingClientRect();
        var center = r.left + r.width / 2;
        var nearest = 0, min = Infinity;
        for (var i = 0; i < items.length; i++) {
          var ir = items[i].getBoundingClientRect();
          var d = Math.abs(ir.left + ir.width / 2 - center);
          if (d < min) { min = d; nearest = i; }
        }
        counter.textContent = nearest + 1;
      };

      track.addEventListener('scroll', update, { passive: true });
      update();
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Sticky nav (custom-header.liquid)
  //
  // State classes on .gh-nav:
  //   .is-over-hero  — page has an intro/home hero AND user has not scrolled past it
  //   .is-scrolled   — user scrolled past hero (or no hero on page)
  //   .is-solid      — solid first-paint state or always-solid override
  //
  // Liquid renders a safe first-paint class. JS then recomputes the dynamic
  // state unless sticky_solid_only explicitly requests a fixed solid header.
  //
  // Idempotent: data-sticky-nav-init guard prevents double-binding.
  // ─────────────────────────────────────────────────────────────
  function initStickyNav(root) {
    root = root || document;
    var nav = root.querySelector('[data-sticky-nav]:not([data-sticky-nav-init])');
    if (!nav) return;
    nav.setAttribute('data-sticky-nav-init', '');

    // The initial .is-solid class can also mean "not the index template".
    // Only the explicit schema override should disable scroll-state detection.
    if (nav.getAttribute('data-sticky-solid-only') === 'true') return;

    var hero = document.querySelector('.gh-life-intro-video, .gh-hero');

    function applyState() {
      var isMobileStoryVideo = nav.classList.contains('is-story-video-nav') && window.matchMedia('(max-width: 767px)').matches;
      if (isMobileStoryVideo) {
        nav.classList.remove('is-over-hero', 'is-scrolled');
        nav.classList.add('is-solid');
        return;
      }
      if (!hero) {
        // No hero on this page: always solid
        nav.classList.remove('is-over-hero', 'is-scrolled');
        nav.classList.add('is-solid');
        return;
      }
      var heroBottom = hero.getBoundingClientRect().bottom;
      if (heroBottom > 0) {
        nav.classList.add('is-over-hero');
        nav.classList.remove('is-scrolled', 'is-solid');
      } else {
        nav.classList.remove('is-over-hero');
        nav.classList.add('is-scrolled');
      }
    }

    applyState();
    window.addEventListener('scroll', applyState, { passive: true });
    window.addEventListener('resize', applyState, { passive: true });
  }

  // ─────────────────────────────────────────────────────────────
  // PDP gallery (custom-pdp-main.liquid)
  //
  // Thumb click → update main image src + active states on thumbs + dots.
  // Scoped to each [data-pdp-gallery] so multiple PDPs on one page are isolated.
  // Idempotent via data-pdp-gallery-init guard.
  // ─────────────────────────────────────────────────────────────
  function initPdpGallery(root) {
    root = root || document;
    root.querySelectorAll('[data-pdp-gallery]:not([data-pdp-gallery-init])').forEach(function (gallery) {
      gallery.setAttribute('data-pdp-gallery-init', '');
      var mainEl = gallery.querySelector('[data-pdp-main]');
      var mainImg = mainEl && mainEl.querySelector('img');
      var mainVideo = mainEl && mainEl.querySelector('[data-pdp-video]');
      var thumbsEl = gallery.querySelector('[data-pdp-thumbs]');
      var dotsEl = gallery.querySelector('[data-pdp-dots]');
      if (!thumbsEl) return;

      function activateThumb(thumb) {
        // Update thumb active states
        var allThumbs = thumbsEl.querySelectorAll('[data-pdp-thumb]');
        var idx = 0;
        allThumbs.forEach(function (t, i) {
          t.classList.toggle('is-active', t === thumb);
          if (t === thumb) idx = i;
        });
        // Re-query main slot every call — avoids stale closures if DOM is late / section-reloaded
        var mainEl2 = gallery.querySelector('[data-pdp-main]');
        var img = mainEl2 && mainEl2.querySelector('img');
        var picture = img && img.closest('.gh-pdp-gallery__picture');
        var vid = mainEl2 && mainEl2.querySelector('[data-pdp-video]');
        // Update main media — a video thumb plays the video in-place; an image thumb shows image
        var videoSrc = thumb.getAttribute('data-pdp-video-src');
        if (videoSrc && vid) {
          // Hide the image, reveal the video element
          if (picture) picture.hidden = true;
          if (img) img.hidden = true;
          if (mainEl2) {
            mainEl2.removeAttribute('role');
            mainEl2.removeAttribute('tabindex');
            mainEl2.removeAttribute('aria-label');
          }
          vid.removeAttribute('hidden');
          vid.hidden = false;
          vid.muted = true;
          vid.defaultMuted = true;
          vid.loop = true;
          vid.playsInline = true;
          vid.setAttribute('aria-label', thumb.getAttribute('data-pdp-alt') || '');
          // Only reload if src changed (avoids restarting an already-playing clip)
          if (vid.getAttribute('src') !== videoSrc) {
            vid.src = videoSrc;
            vid.load();
          }
          var playPromise = vid.play();
          if (playPromise && playPromise.catch) playPromise.catch(function () {});
        } else {
          // Hide video, reveal image
          if (vid) {
            vid.pause();
            vid.hidden = true;
          }
          if (picture) picture.hidden = false;
          var fullSrc = thumb.getAttribute('data-pdp-full');
          var mobileSrc = thumb.getAttribute('data-pdp-mobile') || fullSrc;
          var fullAlt = thumb.getAttribute('data-pdp-alt') || '';
          if (img) {
            img.removeAttribute('hidden');
            img.hidden = false;
            var mobileSource = picture && picture.querySelector('source[media]');
            if (mobileSource && mobileSrc) mobileSource.srcset = mobileSrc;
            if (fullSrc) {
              // The server-rendered main <img> carries srcset/sizes from Liquid's
              // image_tag filter. Browsers always honour srcset over src, so
              // updating only .src leaves the OLD responsive image on screen —
              // these attributes must be dropped for the swap to take effect.
              img.removeAttribute('srcset');
              img.removeAttribute('sizes');
              img.src = fullSrc;
              img.alt = fullAlt;
            }
          }
          if (mainEl2) {
            mainEl2.setAttribute('role', 'button');
            mainEl2.setAttribute('tabindex', '0');
            mainEl2.setAttribute('aria-label', mainEl2.getAttribute('data-pdp-lightbox-label') || '');
          }
        }
        // Update dots
        if (dotsEl) {
          dotsEl.querySelectorAll('[data-pdp-dot]').forEach(function (dot, i) {
            var active = i === idx;
            dot.classList.toggle('is-active', active);
            dot.setAttribute('aria-selected', active ? 'true' : 'false');
            dot.setAttribute('tabindex', active ? '0' : '-1');
          });
        }
        // Scroll thumb into view (horizontal on mobile, vertical on desktop)
        try { thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' }); } catch (e) {}
      }

      // Bind thumb clicks
      thumbsEl.querySelectorAll('[data-pdp-thumb]').forEach(function (thumb) {
        thumb.addEventListener('click', function () { activateThumb(thumb); });
      });

      // Bind dot clicks
      if (dotsEl) {
        var dots = Array.from(dotsEl.querySelectorAll('[data-pdp-dot]'));
        dots.forEach(function (dot) {
          var idx = parseInt(dot.getAttribute('data-pdp-dot'), 10);
          dot.addEventListener('click', function () {
            var target = thumbsEl.querySelectorAll('[data-pdp-thumb]')[idx];
            if (target) activateThumb(target);
          });
        });
        dotsEl.addEventListener('keydown', function (event) {
          var current = dots.indexOf(document.activeElement);
          if (current < 0) return;
          var next = current;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % dots.length;
          else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (current - 1 + dots.length) % dots.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = dots.length - 1;
          else return;
          event.preventDefault();
          dots[next].focus();
          dots[next].click();
        });
      }

      // Touch swipe on the main slot — switch to prev/next thumb (mobile).
      // touch-action:pan-y on .gh-pdp-gallery__main leaves horizontal gestures to JS.
      if (mainEl) {
        var swStartX = 0, swStartY = 0, swiping = false;
        mainEl.addEventListener('touchstart', function (e) {
          if (!e.touches[0]) return;
          swStartX = e.touches[0].clientX;
          swStartY = e.touches[0].clientY;
          swiping = true;
        }, { passive: true });
        mainEl.addEventListener('touchend', function (e) {
          if (!swiping) return;
          swiping = false;
          var t = e.changedTouches[0];
          if (!t) return;
          var dx = t.clientX - swStartX, dy = t.clientY - swStartY;
          if (Math.abs(dx) <= 40 || Math.abs(dx) <= Math.abs(dy)) return;
          var list = thumbsEl.querySelectorAll('[data-pdp-thumb]');
          if (!list.length) return;
          var cur = 0;
          for (var i = 0; i < list.length; i++) {
            if (list[i].classList.contains('is-active')) { cur = i; break; }
          }
          var next = (cur + (dx < 0 ? 1 : -1) + list.length) % list.length;
          activateThumb(list[next]);
        });
      }
    });
  }

  // ─────────────────────────────────────────────────────────────
  // PDP variant select (custom-pdp-main.liquid)
  //
  // User clicks radio swatch label → find matching variant by building
  // the option combination → update hidden <select name="id"> value →
  // dispatch 'change' and synchronize every visible purchase state from the
  // selected option's server-rendered data attributes.
  // Idempotent via data-pdp-variants-init guard.
  // ─────────────────────────────────────────────────────────────
  function initPdpVariantSelect(root) {
    root = root || document;
    root.querySelectorAll('[data-pdp-variants]:not([data-pdp-variants-init])').forEach(function (variantsEl) {
      variantsEl.setAttribute('data-pdp-variants-init', '');
      var select = variantsEl.querySelector('[data-pdp-variant-select]');
      if (!select) return;
      var form = variantsEl.closest('[data-pdp-form]');

      function syncVariantUi() {
        if (!form) return;
        var selectedOption = select.options[select.selectedIndex];
        if (!selectedOption) return;

        var available = selectedOption.getAttribute('data-available') === 'true' && !selectedOption.disabled;
        var priceEl = form.querySelector('[data-pdp-price]');
        var discountEl = form.querySelector('[data-pdp-discount]');
        var comparePriceEl = form.querySelector('[data-pdp-compare-price]');
        var stockEl = form.querySelector('[data-pdp-stock]');
        var stockInEl = form.querySelector('[data-pdp-stock-in]');
        var stockOutEl = form.querySelector('[data-pdp-stock-out]');
        var addButton = form.querySelector('[data-pdp-add-btn]');

        if (priceEl && selectedOption.dataset.price) {
          priceEl.textContent = selectedOption.dataset.price;
        }
        if (discountEl) {
          discountEl.textContent = selectedOption.dataset.discountText || '';
          discountEl.hidden = !selectedOption.dataset.discountText;
        }
        if (comparePriceEl) {
          comparePriceEl.textContent = selectedOption.dataset.comparePrice || '';
          comparePriceEl.hidden = !selectedOption.dataset.comparePrice;
          var compareGroup = comparePriceEl.closest('[data-pdp-compare-group]');
          if (compareGroup) compareGroup.hidden = !selectedOption.dataset.comparePrice;
        }
        if (stockEl) {
          stockEl.classList.toggle('gh-pdp-buybox__stock--out', !available);
        }
        if (stockInEl) stockInEl.hidden = !available;
        if (stockOutEl) stockOutEl.hidden = available;
        if (addButton && addButton.getAttribute('aria-busy') !== 'true') {
          addButton.disabled = !available;
          addButton.textContent = available
            ? (addButton.getAttribute('data-add-label') || '')
            : (addButton.getAttribute('data-sold-out-label') || '');
        }
      }

      select.addEventListener('change', syncVariantUi);

      function syncSelect() {
        // Collect currently checked radio value per option
        var checkedValues = [];
        variantsEl.querySelectorAll('.gh-pdp-buybox__option').forEach(function (fieldset) {
          var checked = fieldset.querySelector('input[type="radio"]:checked');
          checkedValues.push(checked ? checked.value : '');
        });
        // Find the matching <option> by comparing variant title parts
        var options = select.querySelectorAll('option');
        var matched = null;
        options.forEach(function (opt) {
          var title = opt.textContent.trim();
          // Single-option products: title IS the value. Multi: "Value1 / Value2"
          var parts = title.split(' / ').map(function (p) { return p.trim(); });
          var match = checkedValues.every(function (v, i) { return parts[i] === v; });
          if (match && !matched) matched = opt;
        });
        if (matched && matched.value !== select.value) {
          select.value = matched.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (matched) {
          syncVariantUi();
        }
      }

      // Update swatch visual active state + sync select on radio change
      variantsEl.querySelectorAll('.gh-pdp-buybox__swatch').forEach(function (label) {
        label.addEventListener('click', function () {
          var radio = label.querySelector('input[type="radio"]');
          if (!radio) return;
          // Update active class within the same fieldset/swatches group
          var swatches = label.closest('.gh-pdp-buybox__swatches');
          if (swatches) {
            swatches.querySelectorAll('.gh-pdp-buybox__swatch').forEach(function (s) {
              s.classList.remove('is-active');
            });
          }
          label.classList.add('is-active');
          syncSelect();
        });
      });
      syncVariantUi();
    });
  }

  // Preserve landing/attribution query parameters when the color swatch moves
  // between the two product URLs. Product-specific parameters must not cross
  // to the other product because their IDs or render modes may be invalid there.
  function initPdpColorQueryLinks(root) {
    root = root || document;
    if (!window.location.search) return;

    var sourceParams = new URLSearchParams(window.location.search);
    ['variant', 'selling_plan', 'view', 'section_id', 'preview_theme_id'].forEach(function (name) {
      sourceParams.delete(name);
    });
    if (!sourceParams.toString()) return;

    root.querySelectorAll('[data-pdp-color-link]:not([data-pdp-query-init])').forEach(function (link) {
      link.setAttribute('data-pdp-query-init', '');
      var destination;
      try {
        destination = new URL(link.getAttribute('href'), window.location.origin);
      } catch (error) {
        return;
      }
      sourceParams.forEach(function (value, name) {
        if (!destination.searchParams.has(name)) destination.searchParams.append(name, value);
      });
      link.href = destination.href;
    });
  }

  // ─────────────────────────────────────────────────────────────
  // PDP qty stepper (custom-pdp-main.liquid)
  //
  // +/- buttons update the <input type="number" name="quantity"> within
  // the same .gh-pdp-buybox__stepper parent. Clamps to min/max attrs.
  // Idempotent via data-pdp-qty-init guard.
  // ─────────────────────────────────────────────────────────────
  function initPdpQtyStepper(root) {
    root = root || document;
    root.querySelectorAll('[data-pdp-qty-step]:not([data-pdp-qty-init])').forEach(function (btn) {
      btn.setAttribute('data-pdp-qty-init', '');
      btn.addEventListener('click', function () {
        var stepper = btn.closest('.gh-pdp-buybox__stepper');
        if (!stepper) return;
        var input = stepper.querySelector('input[type="number"]');
        if (!input) return;
        var step = parseInt(btn.getAttribute('data-pdp-qty-step'), 10) || 0;
        var min = parseInt(input.getAttribute('min'), 10) || 1;
        var max = parseInt(input.getAttribute('max'), 10) || 99;
        var current = parseInt(input.value, 10) || 1;
        var next = Math.min(max, Math.max(min, current + step));
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  // A single absolute deadline is shared by every visitor. Never restart the
  // countdown from page load, and hide it when the deadline or sale ends.
  function initPdpPromoTimer(root) {
    root = root || document;
    root.querySelectorAll('[data-pdp-promo]:not([data-pdp-promo-init])').forEach(function (timer) {
      timer.setAttribute('data-pdp-promo-init', '');
      var endAt = timer.getAttribute('data-end-at') || '';
      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:Z|[+-]\d{2}:\d{2})$/.test(endAt)) return;
      var endMs = Date.parse(endAt);
      if (!Number.isFinite(endMs) || endMs <= Date.now()) return;

      var fields = {
        days: timer.querySelector('[data-pdp-promo-days]'),
        hours: timer.querySelector('[data-pdp-promo-hours]'),
        minutes: timer.querySelector('[data-pdp-promo-minutes]'),
        seconds: timer.querySelector('[data-pdp-promo-seconds]')
      };
      if (Object.keys(fields).some(function (key) { return !fields[key]; })) return;
      var dayWrap = timer.querySelector('[data-pdp-promo-day-wrap]');
      var form = timer.closest('[data-pdp-form]');
      var discountBadge = form && form.querySelector('[data-pdp-discount]');
      var stockIn = form && form.querySelector('[data-pdp-stock-in]');
      var interval;

      function render() {
        if (!timer.isConnected) {
          window.clearInterval(interval);
          return;
        }
        var remaining = Math.max(0, Math.ceil((endMs - Date.now()) / 1000));
        if (remaining === 0) {
          timer.hidden = true;
          window.clearInterval(interval);
          return;
        }
        if (!discountBadge || discountBadge.hidden || (stockIn && stockIn.hidden)) {
          timer.hidden = true;
          return;
        }
        var days = Math.floor(remaining / 86400);
        fields.days.textContent = String(days).padStart(2, '0');
        if (dayWrap) dayWrap.hidden = days === 0;
        fields.hours.textContent = String(Math.floor((remaining % 86400) / 3600)).padStart(2, '0');
        fields.minutes.textContent = String(Math.floor((remaining % 3600) / 60)).padStart(2, '0');
        fields.seconds.textContent = String(remaining % 60).padStart(2, '0');
        timer.hidden = false;
      }

      if (form) {
        var variantSelect = form.querySelector('[data-pdp-variant-select]');
        if (variantSelect) variantSelect.addEventListener('change', render);
      }
      interval = window.setInterval(render, 1000);
      render();
    });
  }

  // ─────────────────────────────────────────────────────────────
  // PDP Add-to-Cart (custom-pdp-main.liquid)
  //
  // Keeps the shopper on the PDP so Shopify Web Pixels and Google tags can
  // finish sending the native add_to_cart event. Uses Dawn's cart-notification
  // component when present; otherwise the untouched native form submission is
  // allowed to continue as a fail-safe.
  // ─────────────────────────────────────────────────────────────
  function initPdpAddToCart(root) {
    root = root || document;
    root.querySelectorAll('[data-pdp-form]:not([data-pdp-atc-init])').forEach(function (form) {
      form.setAttribute('data-pdp-atc-init', '');

      form.addEventListener('submit', function (event) {
        var cart = document.querySelector('cart-notification');
        var button = form.querySelector('[data-pdp-add-btn]');
        if (!cart || typeof cart.getSectionsToRender !== 'function' ||
          typeof cart.getSectionInnerHTML !== 'function' || typeof cart.open !== 'function' ||
          !button || button.disabled || button.getAttribute('aria-busy') === 'true') {
          return;
        }

        event.preventDefault();
        var errorEl = form.querySelector('[data-pdp-checkout-error]');
        if (errorEl) {
          errorEl.hidden = true;
          errorEl.textContent = '';
        }
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        var stickyButton = document.querySelector('[data-pdp-sticky-add]');
        var statusEl = form.querySelector('[data-pdp-add-status]');
        var addLabel = button.getAttribute('data-add-label') || button.textContent.trim();
        var soldOutLabel = button.getAttribute('data-sold-out-label') || addLabel;
        var addingLabel = button.getAttribute('data-adding-label') || addLabel;
        var addedLabel = button.getAttribute('data-added-label') || addLabel;

        function setAddButtons(label, disabled) {
          button.textContent = label;
          button.disabled = disabled;
          if (stickyButton) {
            stickyButton.textContent = label;
            stickyButton.disabled = disabled;
          }
        }

        function restoreAddButtons() {
          var variantSelect = form.querySelector('[data-pdp-variant-select]');
          var selectedOption = variantSelect && variantSelect.options[variantSelect.selectedIndex];
          var unavailable = !!selectedOption &&
            (selectedOption.disabled || selectedOption.getAttribute('data-available') !== 'true');
          setAddButtons(unavailable ? soldOutLabel : addLabel, unavailable);
          button.removeAttribute('aria-busy');
        }

        function presentAddedState() {
          setAddButtons(addedLabel, true);
          if (statusEl) {
            statusEl.textContent = statusEl.getAttribute('data-success-message') || addedLabel;
            statusEl.hidden = false;
          }
        }

        function showAddError(error, fallbackOnly) {
          if (!errorEl) return;
          errorEl.textContent = fallbackOnly
            ? (errorEl.getAttribute('data-add-fallback-message') || '')
            : getErrorFallback(errorEl, 'data-add-fallback-message', error);
          errorEl.hidden = false;
        }

        setAddButtons(addingLabel, true);
        if (statusEl) {
          statusEl.hidden = true;
          statusEl.textContent = '';
        }

        var formData = new FormData(form);
        formData.delete('sections');
        formData.delete('sections_url');
        formData.append('sections', cart.getSectionsToRender().map(function (section) {
          return section.id;
        }).join(','));
        formData.append('sections_url', window.location.pathname);
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(button);
        var variantId = String(formData.get('id') || '').trim();
        var mutationSucceeded = false;
        var fallbackNavigationPending = false;
        var requestStarted = false;
        var baselineReady = false;
        var baselineVariantQuantity = 0;

        fetchCartState()
          .then(function (baselineCartState) {
            baselineVariantQuantity = getCartVariantQuantity(baselineCartState, variantId);
            baselineReady = true;
            requestStarted = true;
            return fetch(getThemeRoute('cart_add_url', 'cart/add.js'), {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              },
              body: formData
            });
          })
          .then(function (response) {
            return response.json().then(function (payload) {
              if (!response.ok || payload.status) {
                var serverError = new Error(getCartErrorMessage(payload));
                serverError.serverRejected = true;
                throw serverError;
              }
              return payload;
            });
          })
          .then(function (payload) {
            mutationSucceeded = true;
            cart.cartItemKey = payload.key;
            var sections = cart.getSectionsToRender();
            var sectionsReady = payload.sections && sections.every(function (section) {
              return document.getElementById(section.id) && payload.sections[section.id];
            });
            if (!sectionsReady) {
              fallbackNavigationPending = true;
              window.location.assign(getThemeRoute('cart_url', 'cart'));
              return;
            }
            try {
              sections.forEach(function (section) {
                var destination = document.getElementById(section.id);
                var html = payload.sections[section.id];
                destination.innerHTML = cart.getSectionInnerHTML(html, section.selector);
              });
              cart.open();
              presentAddedState();
            } catch (uiError) {
              fallbackNavigationPending = true;
              window.location.assign(getThemeRoute('cart_url', 'cart'));
              return;
            }

            return fetchCartState()
              .then(updateCartBadges)
              .catch(function () {
                // The mutation succeeded, but the visible badge cannot be reconciled.
                // Use the cart page as a read-only truth fallback; never retry the add.
                fallbackNavigationPending = true;
                window.location.assign(getThemeRoute('cart_url', 'cart'));
              });
          })
          .catch(function (error) {
            if (mutationSucceeded) {
              fallbackNavigationPending = true;
              window.location.assign(getThemeRoute('cart_url', 'cart'));
              return;
            }
            if (!requestStarted || !baselineReady) {
              showAddError(error, true);
              return;
            }
            if (error && error.serverRejected) {
              showAddError(error, false);
              return;
            }

            // The add response was uncertain (network/parse failure). Read the
            // cart once and compare this variant's total quantity with the
            // pre-request baseline. Never retry the mutation blindly.
            return fetchCartState()
              .then(function (cartState) {
                updateCartBadges(cartState);
                var currentVariantQuantity = getCartVariantQuantity(cartState, variantId);
                if (currentVariantQuantity <= baselineVariantQuantity) {
                  showAddError(error, false);
                  return;
                }

                mutationSucceeded = true;
                return refreshCartTruth(cart, variantId, cartState)
                  .then(function () {
                    cart.open();
                    presentAddedState();
                  })
                  .catch(function () {
                    fallbackNavigationPending = true;
                    window.location.assign(getThemeRoute('cart_url', 'cart'));
                  });
              })
              .catch(function () {
                // Truth could not be read, so keep the buttons locked and show
                // the cart page instead of inviting a duplicate add.
                fallbackNavigationPending = true;
                window.location.assign(getThemeRoute('cart_url', 'cart'));
              });
          })
          .finally(function () {
            if (fallbackNavigationPending) return;
            if (mutationSucceeded) {
              window.setTimeout(function () {
                restoreAddButtons();
                if (statusEl) {
                  statusEl.hidden = true;
                  statusEl.textContent = '';
                }
              }, 1600);
            } else {
              restoreAddButtons();
            }
          });
      });
    });
  }

  // PDP direct checkout
  //
  // Creates a separate Storefront Cart for only the currently selected
  // variant and quantity. The shopper's normal Ajax cart is never mutated.
  // The normal Add to Cart submit is unchanged.
  // ─────────────────────────────────────────────────────────────
  function initPdpDirectCheckout(root) {
    root = root || document;
    root.querySelectorAll('[data-pdp-checkout-btn]:not([data-pdp-checkout-init])').forEach(function (btn) {
      btn.setAttribute('data-pdp-checkout-init', '');
      var form = btn.closest('[data-pdp-form]');
      if (!form) return;

      var variantSelect = form.querySelector('[data-pdp-variant-select]');
      function selectedVariantIsAvailable() {
        if (!variantSelect) return true;
        var selectedOption = variantSelect.options[variantSelect.selectedIndex];
        return !!selectedOption && !selectedOption.disabled;
      }
      function syncAvailability() {
        if (btn.getAttribute('aria-busy') === 'true') return;
        btn.disabled = !selectedVariantIsAvailable();
      }
      if (variantSelect) {
        variantSelect.addEventListener('change', syncAvailability);
      }

      window.addEventListener('pageshow', function (event) {
        if (!event.persisted) return;
        btn.removeAttribute('aria-busy');
        syncAvailability();
      });

      btn.addEventListener('click', function () {
        if (btn.disabled || btn.getAttribute('aria-busy') === 'true') return;

        var errorEl = form.querySelector('[data-pdp-checkout-error]');
        var supportLink = form.querySelector('[data-pdp-checkout-support]');
        if (errorEl) {
          errorEl.hidden = true;
          errorEl.textContent = '';
        }
        if (supportLink) supportLink.hidden = true;

        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');

        var formData = new FormData(form);
        var variantId = String(formData.get('id') || '').trim();
        var quantityInput = form.querySelector('input[name="quantity"]');
        var quantity = quantityInput ? Number(quantityInput.value) : 1;
        var quantityMin = quantityInput && quantityInput.min !== '' ? Number(quantityInput.min) : 1;
        var quantityMax = quantityInput && quantityInput.max !== '' ? Number(quantityInput.max) : 99;

        if (!/^\d+$/.test(variantId) || !Number.isInteger(quantity) ||
          !Number.isFinite(quantityMin) || !Number.isFinite(quantityMax) ||
          quantity < quantityMin || quantity > quantityMax ||
          (quantityInput && !quantityInput.checkValidity())) {
          if (errorEl) {
            errorEl.textContent = errorEl.getAttribute('data-checkout-fallback-message') || '';
            errorEl.hidden = false;
          }
          btn.removeAttribute('aria-busy');
          syncAvailability();
          return;
        }

        var locale = ((window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'en')
          .toLowerCase()
          .replace('_', '-')
          .split('-')[0];
        var languageCodes = { en: 'EN', fr: 'FR', de: 'DE', es: 'ES' };
        var languageCode = languageCodes[locale] || 'EN';
        var countryCode = ((window.Shopify && window.Shopify.country) || 'US').toUpperCase();
        if (!/^[A-Z]{2}$/.test(countryCode)) countryCode = 'US';

        var query = [
          'mutation CreateDirectCart($input: CartInput!, $country: CountryCode!, $language: LanguageCode!)',
          '@inContext(country: $country, language: $language) {',
          '  cartCreate(input: $input) {',
          '    cart {',
          '      checkoutUrl',
          '      totalQuantity',
          '      lines(first: 2) {',
          '        nodes {',
          '          quantity',
          '          merchandise { ... on ProductVariant { id } }',
          '        }',
          '      }',
          '    }',
          '    userErrors { message }',
          '  }',
          '}'
        ].join('\n');
        var controller = new AbortController();
        var timeoutId = window.setTimeout(function () {
          controller.abort();
        }, 12000);

        fetch('/api/2026-01/graphql.json', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          signal: controller.signal,
          body: JSON.stringify({
            query: query,
            variables: {
              input: {
                lines: [{
                  merchandiseId: 'gid://shopify/ProductVariant/' + variantId,
                  quantity: quantity
                }],
                buyerIdentity: { countryCode: countryCode }
              },
              country: countryCode,
              language: languageCode
            }
          })
        })
          .then(function (response) {
            if (!response.ok) throw new Error('');
            return response.json();
          })
          .then(function (payload) {
            window.clearTimeout(timeoutId);
            if (payload && Array.isArray(payload.errors) && payload.errors.length) {
              throw new Error(payload.errors[0].message || '');
            }
            var result = payload && payload.data && payload.data.cartCreate;
            var cart = result && result.cart;
            var lines = cart && cart.lines && cart.lines.nodes;
            var expectedMerchandiseId = 'gid://shopify/ProductVariant/' + variantId;
            var validLine = Array.isArray(lines) && lines.length === 1 &&
              lines[0].quantity === quantity &&
              lines[0].merchandise &&
              lines[0].merchandise.id === expectedMerchandiseId;
            var checkoutUrl;

            if (!cart || !cart.checkoutUrl || cart.totalQuantity !== quantity || !validLine ||
              (result.userErrors && result.userErrors.length)) {
              var message = result && result.userErrors && result.userErrors[0] && result.userErrors[0].message;
              throw new Error(message || '');
            }

            checkoutUrl = new URL(cart.checkoutUrl, window.location.origin);
            if (checkoutUrl.protocol !== 'https:' || checkoutUrl.hostname !== window.location.hostname) {
              throw new Error('');
            }

            window.location.assign(checkoutUrl.href);
          })
          .catch(function (error) {
            window.clearTimeout(timeoutId);
            if (errorEl) {
              errorEl.textContent = getErrorFallback(errorEl, 'data-checkout-fallback-message', error);
              errorEl.hidden = false;
            }
            btn.textContent = btn.getAttribute('data-retry-label') || btn.textContent;
            if (supportLink) supportLink.hidden = false;
            btn.removeAttribute('aria-busy');
            syncAvailability();
          });
      });
    });
  }

  // ─────────────────────────────────────────────────────────────
  // PDP USE CASES — chip-tab switcher
  // Scoped by [data-use-tabs] attribute so multiple instances
  // on a page (if any) stay independent.
  // ─────────────────────────────────────────────────────────────
  function initPdpUseTabs(root) {
    var groups = (root || document).querySelectorAll('[data-use-tabs]');
    groups.forEach(function (chipList) {
      var scopeId = chipList.getAttribute('data-use-tabs');
      var section = chipList.closest('.gh-pdp-use');
      if (!section) return;
      var panels = section.querySelectorAll('[data-use-panel]');

      chipList.addEventListener('click', function (e) {
        var chip = e.target.closest('[data-use-tab]');
        if (!chip) return;
        var targetId = chip.getAttribute('data-use-tab');

        // Update chip states
        chipList.querySelectorAll('[data-use-tab]').forEach(function (c) {
          c.classList.toggle('is-active', c === chip);
          c.setAttribute('aria-selected', c === chip ? 'true' : 'false');
        });

        // Update panel states
        panels.forEach(function (p) {
          var match = p.getAttribute('data-use-panel') === targetId;
          p.classList.toggle('is-active', match);
        });
      });
    });
  }

  // PDP scene wall — keep the four founder-selected use cases visible on
  // phones and progressively disclose the remaining three. Desktop/tablet
  // keep all seven scenes visible.
  function initPdpSceneFold(root) {
    root = root || document;
    root.querySelectorAll('.gh-scene--editorial:not([data-scene-fold-init])').forEach(function (scene) {
      scene.setAttribute('data-scene-fold-init', '');
      var grid = scene.querySelector('.gh-scene__grid');
      var toggle = scene.querySelector('[data-scene-toggle]');
      if (!grid || !toggle) return;

      var tiles = Array.prototype.slice.call(grid.querySelectorAll('[data-scene-tile]'));
      var priority = tiles.filter(function (tile) {
        return tile.hasAttribute('data-scene-priority');
      });
      var remainder = tiles.filter(function (tile) {
        return !tile.hasAttribute('data-scene-priority');
      });
      var ordered = priority.concat(remainder);

      ordered.forEach(function (tile, index) {
        tile.classList.toggle('is-extra-use', index >= 4);
        grid.appendChild(tile);
      });

      if (ordered.length <= 4) {
        toggle.hidden = true;
        return;
      }

      toggle.addEventListener('click', function () {
        var expanded = scene.classList.toggle('is-expanded');
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        toggle.textContent = expanded
          ? toggle.getAttribute('data-less-label')
          : toggle.getAttribute('data-more-label');
      });
    });
  }

  // ─────────────────────────────────────────────────────────────
  // PDP ABOUT — accessible Highlights / Best Uses tabs
  // Native <details> elements inside Highlights own disclosure.
  // ─────────────────────────────────────────────────────────────
  function initPdpAboutTabs(root) {
    var groups = (root || document).querySelectorAll('[data-pdp-about-tabs]:not([data-pdp-about-tabs-ready])');

    groups.forEach(function (group) {
      group.setAttribute('data-pdp-about-tabs-ready', 'true');

      var tabs = Array.prototype.slice.call(group.querySelectorAll('[data-pdp-about-tab]'));
      var panels = Array.prototype.slice.call(group.querySelectorAll('[data-pdp-about-panel]'));
      if (!tabs.length || tabs.length !== panels.length) return;

      function activateTab(tab, moveFocus) {
        var panelId = tab.getAttribute('aria-controls');

        tabs.forEach(function (candidate) {
          var isActive = candidate === tab;
          candidate.classList.toggle('is-active', isActive);
          candidate.setAttribute('aria-selected', isActive ? 'true' : 'false');
          candidate.tabIndex = isActive ? 0 : -1;
        });

        panels.forEach(function (panel) {
          var isActive = panel.id === panelId;
          panel.classList.toggle('is-active', isActive);
          panel.hidden = !isActive;
        });

        if (moveFocus) {
          tab.focus();
          tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      }

      tabs.forEach(function (tab, index) {
        tab.addEventListener('click', function () {
          activateTab(tab, false);
        });

        tab.addEventListener('keydown', function (event) {
          var nextIndex = index;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
          else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
          else if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = tabs.length - 1;
          else return;

          event.preventDefault();
          activateTab(tabs[nextIndex], true);
        });
      });

      activateTab(tabs.find(function (tab) {
        return tab.getAttribute('aria-selected') === 'true';
      }) || tabs[0], false);
    });
  }


  // Group only the four adjacent feature sections on mobile. Moving the original
  // nodes preserves their content, translations and anchors; desktop/no-JS keeps
  // the original stack. Editor unload restores nodes before Shopify replaces them.
  var pdpFeatureCarousel = null;
  var pdpFeatureCarouselMedia = null;
  var pdpFeatureCarouselIndex = 0;

  function revealPdpFeatureHash() {
    var id;
    try { id = decodeURIComponent(window.location.hash.slice(1)); } catch (error) { return; }
    var target = id && document.getElementById(id);
    if (!target) return;
    var index = Array.from(document.querySelectorAll('[data-pdp-feature-slide]')).findIndex(function (feature) {
      return feature === target || feature.contains(target);
    });
    if (index < 0) return;
    pdpFeatureCarouselIndex = index;
    if (pdpFeatureCarousel) pdpFeatureCarousel.reveal(target);
  }

  function initPdpFeatureCarousel() {
    var currentFeatures = Array.from(document.querySelectorAll('[data-pdp-feature-slide]'));
    if (pdpFeatureCarousel && pdpFeatureCarouselMedia.matches &&
      currentFeatures.length === pdpFeatureCarousel.features.length &&
      currentFeatures.every(function (feature, index) {
        return feature === pdpFeatureCarousel.features[index] &&
          feature.closest('.shopify-section').parentElement === pdpFeatureCarousel.track;
      })) return;
    if (pdpFeatureCarousel) {
      pdpFeatureCarousel.destroy();
      pdpFeatureCarousel = null;
    }
    var features = Array.from(document.querySelectorAll('[data-pdp-feature-slide]'));
    if (features.length !== 4) return;
    var sections = features.map(function (feature) { return feature.closest('.shopify-section'); });
    var parent = sections[0] && sections[0].parentElement;
    if (!parent || parent.id !== 'MainContent' || sections.some(function (section, index) {
      return !section || section.parentElement !== parent ||
        (index > 0 && sections[index - 1].nextElementSibling !== section);
    })) return;

    if (!pdpFeatureCarouselMedia) {
      pdpFeatureCarouselMedia = window.matchMedia('(max-width: 767px)');
      pdpFeatureCarouselMedia.addEventListener('change', initPdpFeatureCarousel);
      // Keep fragment intent across desktop/mobile transitions, without
      // replaying a stale hash on unrelated editor section reloads.
      window.addEventListener('hashchange', revealPdpFeatureHash);
      revealPdpFeatureHash();
      document.addEventListener('shopify:section:reorder', initPdpFeatureCarousel);
      document.addEventListener('shopify:section:unload', function (event) {
        if (pdpFeatureCarousel && pdpFeatureCarousel.wrapper.contains(event.target)) {
          pdpFeatureCarousel.destroy();
          pdpFeatureCarousel = null;
        }
      });
      document.addEventListener('shopify:section:select', function (event) {
        if (pdpFeatureCarousel) pdpFeatureCarousel.reveal(event.target);
      });
    }
    if (!pdpFeatureCarouselMedia.matches) return;

    var label = features[0].getAttribute('data-pdp-feature-slider-label');
    var wrapper = document.createElement('div');
    wrapper.className = 'gh-pdp-feature-carousel';
    wrapper.setAttribute('role', 'region');
    wrapper.setAttribute('aria-label', label);
    var track = document.createElement('div');
    track.className = 'gh-pdp-feature-carousel__track';
    track.id = 'PdpFeatureTrack-' + sections[0].id;
    track.tabIndex = 0;
    track.setAttribute('aria-label', label);
    var nav = document.createElement('div');
    nav.className = 'gh-pdp-feature-carousel__dots';
    nav.setAttribute('role', 'group');
    nav.setAttribute('aria-label', label);
    var dots = sections.map(function (section, index) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'gh-pdp-feature-carousel__dot';
      button.setAttribute('aria-controls', track.id);
      var heading = features[index].querySelector('.gh-lb__title');
      var headingText = heading && heading.textContent.trim();
      button.setAttribute('aria-label', (headingText || features[index].id || label) + ' (' + (index + 1) + '/4)');
      button.addEventListener('click', function () { goTo(index, true); });
      nav.appendChild(button);
      return button;
    });
    var originalHidden = sections.map(function (section) { return section.getAttribute('aria-hidden'); });
    var active = Math.min(pdpFeatureCarouselIndex, sections.length - 1);
    var requested = null;
    var frame = 0;
    var lastWidth = 0;
    parent.insertBefore(wrapper, sections[0]);
    wrapper.appendChild(track);
    sections.forEach(function (section) { track.appendChild(section); });
    wrapper.appendChild(nav);

    function setActive(index) {
      active = index;
      pdpFeatureCarouselIndex = index;
      sections.forEach(function (section, i) { section.setAttribute('aria-hidden', String(i !== index)); });
      dots.forEach(function (dot, i) { dot.setAttribute('aria-current', String(i === index)); });
    }
    function goTo(index, smooth) {
      index = Math.max(0, Math.min(index, sections.length - 1));
      var left = track.scrollLeft + sections[index].getBoundingClientRect().left - track.getBoundingClientRect().left;
      var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      requested = smooth && !reducedMotion ? index : null;
      track.scrollTo({ left: left, behavior: smooth && !reducedMotion ? 'smooth' : 'auto' });
      setActive(index);
    }
    function syncScroll() {
      if (frame) return;
      frame = window.requestAnimationFrame(function () {
        frame = 0;
        var left = track.getBoundingClientRect().left;
        // Keep the requested destination while a smooth scroll crosses other
        // cards; rapid keyboard input must build on the last requested slide.
        if (requested !== null) {
          if (Math.abs(sections[requested].getBoundingClientRect().left - left) > 1) return;
          requested = null;
        }
        var closest = 0;
        sections.forEach(function (section, i) {
          if (Math.abs(section.getBoundingClientRect().left - left) < Math.abs(sections[closest].getBoundingClientRect().left - left)) closest = i;
        });
        setActive(closest);
      });
    }
    function resizeTrack() {
      if (lastWidth === track.clientWidth) return;
      lastWidth = track.clientWidth;
      goTo(active, false);
    }
    function cancelRequested() { requested = null; }
    function reveal(target) {
      var index = sections.findIndex(function (section) { return section === target || section.contains(target); });
      if (index >= 0) goTo(index, false);
    }
    function onKeyDown(event) {
      var next = requested === null ? active : requested;
      if (event.key === 'ArrowRight') next += 1;
      else if (event.key === 'ArrowLeft') next -= 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = sections.length - 1;
      else return;
      event.preventDefault();
      goTo(next, true);
      if (nav.contains(event.target)) dots[active].focus();
    }
    track.addEventListener('scroll', syncScroll, { passive: true });
    track.addEventListener('pointerdown', cancelRequested, { passive: true });
    track.addEventListener('wheel', cancelRequested, { passive: true });
    track.addEventListener('keydown', onKeyDown);
    nav.addEventListener('keydown', onKeyDown);
    var observer = typeof ResizeObserver === 'function' ? new ResizeObserver(resizeTrack) : null;
    if (observer) observer.observe(track);
    else window.addEventListener('resize', resizeTrack);
    pdpFeatureCarousel = {
      wrapper: wrapper,
      track: track,
      features: features,
      reveal: reveal,
      destroy: function () {
        if (frame) window.cancelAnimationFrame(frame);
        if (observer) observer.disconnect();
        else window.removeEventListener('resize', resizeTrack);
        track.removeEventListener('scroll', syncScroll);
        track.removeEventListener('pointerdown', cancelRequested);
        track.removeEventListener('wheel', cancelRequested);
        track.removeEventListener('keydown', onKeyDown);
        nav.removeEventListener('keydown', onKeyDown);
        // Use current children so an editor replacement is restored, not a stale node.
        var restoreParent = wrapper.parentElement;
        Array.from(track.children).forEach(function (section) {
          var index = sections.findIndex(function (original) { return original.id === section.id; });
          if (index >= 0 && originalHidden[index] !== null) section.setAttribute('aria-hidden', originalHidden[index]);
          else section.removeAttribute('aria-hidden');
          if (restoreParent) restoreParent.insertBefore(section, wrapper);
        });
        wrapper.remove();
      }
    };
    resizeTrack();
  }

  // Paid-media sitelinks use stable semantic fragments. Reveal a collapsed
  // benefit row when needed, then correct native fragment scroll for the
  // sticky header.
  function initPdpAnchorNavigation() {
    if (document.documentElement.hasAttribute('data-pdp-anchor-ready')) return;
    document.documentElement.setAttribute('data-pdp-anchor-ready', 'true');

    function revealAnchor() {
      if (!window.location.hash) return;
      var id;
      try { id = decodeURIComponent(window.location.hash.slice(1)); } catch (e) { return; }
      if (!id) return;
      var target = document.getElementById(id);
      if (!target || !target.hasAttribute('data-pdp-anchor')) return;

      var details = target.matches('details') ? target : target.nextElementSibling;
      if (details && details.matches('details.gh-pdp-about__item')) details.open = true;

      var panelTarget = target.getAttribute('data-pdp-about-panel-target');
      var about = target.closest('.gh-pdp-about');
      var panel = panelTarget
        ? about && about.querySelector('[data-pdp-about-panel][id$="-' + panelTarget + '"]')
        : target.closest('[data-pdp-about-panel]');
      // Keep the semantic anchor as the scroll target. It owns the sticky-header
      // scroll margin, while the tab panel itself does not.
      var scrollTarget = target;
      if (panel) {
        var tab = about && about.querySelector('[data-pdp-about-tab][aria-controls="' + panel.id + '"]');
        if (tab) tab.click();
      }

      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          scrollTarget.scrollIntoView({ block: 'start' });
        });
      });
    }

    window.addEventListener('hashchange', revealAnchor);
    revealAnchor();
  }

  // ─────────────────────────────────────────────────────────────
  // PDP Lightbox (custom-pdp-main.liquid)
  //
  // Full-screen image/video viewer triggered by clicking the main
  // gallery image. Scoped to [data-pdp-gallery] galleries found
  // within root. Idempotent via data-pdp-lightbox-init guard.
  //
  // Attribute mapping from our thumb buttons to lightbox slide data:
  //   type  <- data-pdp-media-type  ('image' | 'video')
  //   full  <- data-pdp-full        (1200px image URL)  [images]
  //         <- data-pdp-video-src   (mp4 URL)           [videos]
  //   thumb <- data-pdp-full        (preview image)     [videos]
  //   label <- data-pdp-alt
  //
  // V13 source: Goehners_Mobile_v13.html lines 24161-24358.
  // Adapted: removed black/white forEach loop (Shopify = 1 product
  // per PDP); all DOM refs use data-* attributes (not getElementById);
  // trigger bound to main <img> only (avoids video control collision).
  // ─────────────────────────────────────────────────────────────
  function initPdpLightbox(root) {
    root = root || document;
    root.querySelectorAll('[data-pdp-gallery]:not([data-pdp-lightbox-init])').forEach(function (gallery) {
      gallery.setAttribute('data-pdp-lightbox-init', '');

      var lb = gallery.closest('[id^="SectionPdpMain"]') && gallery.closest('[id^="SectionPdpMain"]').parentElement
        ? document.querySelector('[data-pdp-lightbox]')
        : document.querySelector('[data-pdp-lightbox]');
      if (!lb) return;

      var imgEl = lb.querySelector('[data-lb-img]');
      var vidEl = lb.querySelector('[data-lb-video]');
      var thumbsRail = lb.querySelector('[data-lb-thumbs]');
      var stageEl = lb.querySelector('[data-lb-stage]');
      var zoomEl = lb.querySelector('[data-lb-zoom]');
      var thumbsContainer = gallery.querySelector('[data-pdp-thumbs]');
      var mainImg = gallery.querySelector('[data-pdp-main] img');
      var triggerEl = gallery.querySelector('[data-pdp-lightbox-trigger]');

      if (!imgEl || !vidEl || !thumbsContainer || !mainImg || !triggerEl) return;

      var slides = [];
      var idx = 0;

      // Zoom + pan state
      var ZOOM_SCALE = 2;
      var zoomed = false;
      var panX = 0, panY = 0;
      var dragging = false;
      var dragStart = null;
      var movedDuringDrag = false;
      var activeOpener = null;
      var inertedBackground = [];
      var lbInitiallyInert = lb.hasAttribute('inert');
      var ownsBodyOpenClass = false;
      var listeners = [];
      var destroyed = false;

      function listen(target, type, handler, options) {
        if (!target) return;
        target.addEventListener(type, handler, options);
        listeners.push(function () { target.removeEventListener(type, handler, options); });
      }

      function setBackgroundInert() {
        var branch = lb;
        while (branch && branch.parentElement) {
          var parent = branch.parentElement;
          Array.from(parent.children).forEach(function (sibling) {
            if (sibling === branch || sibling.hasAttribute('inert')) return;
            sibling.setAttribute('inert', '');
            inertedBackground.push(sibling);
          });
          if (parent === document.body) break;
          branch = parent;
        }
      }

      function restoreBackground() {
        inertedBackground.forEach(function (element) { element.removeAttribute('inert'); });
        inertedBackground = [];
      }

      function applyTransform() {
        if (zoomed) {
          imgEl.style.transform = 'scale(' + ZOOM_SCALE + ') translate(' + (panX / ZOOM_SCALE) + 'px, ' + (panY / ZOOM_SCALE) + 'px)';
        } else {
          imgEl.style.transform = '';
        }
      }

      function resetZoom() {
        zoomed = false; panX = 0; panY = 0; dragging = false; dragStart = null;
        lb.classList.remove('is-zoomed', 'is-panning');
        applyTransform();
      }

      function render() {
        if (!slides.length) return;
        resetZoom();
        var s = slides[idx];
        if (s.type === 'video') {
          lb.classList.add('is-video');
          vidEl.src = s.full;
          vidEl.play().catch(function () {});
        } else {
          lb.classList.remove('is-video');
          try { vidEl.pause(); } catch (e) {}
          vidEl.removeAttribute('src');
          imgEl.src = s.full;
          imgEl.alt = s.label || '';
        }
        if (thumbsRail) {
          var ts = thumbsRail.children;
          for (var i = 0; i < ts.length; i++) {
            ts[i].classList.toggle('is-active', i === idx);
          }
          var active = ts[idx];
          if (active && active.scrollIntoView) {
            active.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
          }
        }
      }

      function step(dir) {
        if (!slides.length) return;
        idx = (idx + dir + slides.length) % slides.length;
        render();
      }

      function buildThumbs() {
        if (!thumbsRail) return;
        thumbsRail.innerHTML = '';
        slides.forEach(function (s, i) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'gh-lightbox__thumb';
          btn.setAttribute('aria-label', s.label || ('Image ' + (i + 1)));
          btn.dataset.type = s.type || 'image';
          var poster = s.thumb || s.full;
          var el = document.createElement('img');
          el.src = poster;
          el.alt = '';
          el.loading = 'lazy';
          btn.appendChild(el);
          btn.dataset.lbIndex = String(i);
          thumbsRail.appendChild(btn);
        });
      }

      function open(startIdx) {
        if (destroyed || lb.classList.contains('is-open')) return;
        slides = Array.from(thumbsContainer.querySelectorAll('.gh-pdp-gallery__thumb')).map(function (t) {
          var type = t.dataset.pdpMediaType || 'image';
          var full = type === 'video' ? (t.dataset.pdpVideoSrc || t.dataset.pdpFull) : t.dataset.pdpFull;
          var thumb = t.dataset.pdpFull;
          var label = t.dataset.pdpAlt || '';
          return { type: type, full: full, thumb: thumb, label: label };
        });
        idx = Math.max(0, Math.min(startIdx || 0, slides.length - 1));
        buildThumbs();
        render();
        activeOpener = triggerEl;
        lb.removeAttribute('inert');
        lb.classList.add('is-open');
        lb.setAttribute('aria-hidden', 'false');
        ownsBodyOpenClass = !document.body.classList.contains('has-lightbox-open');
        document.body.classList.add('has-lightbox-open');
        setBackgroundInert();
        var closeButton = lb.querySelector('[data-lb-close]');
        if (typeof trapFocus === 'function') trapFocus(lb, closeButton || lb);
        else if (closeButton) closeButton.focus();
      }

      function close(restoreFocus) {
        var wasOpen = lb.classList.contains('is-open');
        if (wasOpen) {
          resetZoom();
          lb.classList.remove('is-open');
          lb.setAttribute('aria-hidden', 'true');
          lb.setAttribute('inert', '');
          try { vidEl.pause(); } catch (e) {}
          vidEl.removeAttribute('src');
        }
        if (ownsBodyOpenClass) document.body.classList.remove('has-lightbox-open');
        ownsBodyOpenClass = false;
        restoreBackground();
        var focusTarget = restoreFocus === false || !activeOpener || !activeOpener.isConnected ? null : activeOpener;
        if (wasOpen && typeof removeTrapFocus === 'function') removeTrapFocus(focusTarget, lb);
        else if (wasOpen && focusTarget) focusTarget.focus();
        activeOpener = null;
      }

      // Click-to-zoom + pointer drag-to-pan
      if (zoomEl) {
        listen(zoomEl, 'click', function (e) {
          if (movedDuringDrag) { movedDuringDrag = false; return; }
          if (lb.classList.contains('is-video')) return;
          zoomed = !zoomed;
          if (!zoomed) { panX = 0; panY = 0; }
          lb.classList.toggle('is-zoomed', zoomed);
          applyTransform();
        });
        listen(zoomEl, 'pointerdown', function (e) {
          if (!zoomed || lb.classList.contains('is-video')) return;
          dragging = true;
          movedDuringDrag = false;
          dragStart = { x: e.clientX - panX, y: e.clientY - panY };
          try { zoomEl.setPointerCapture(e.pointerId); } catch (err) {}
          lb.classList.add('is-panning');
        });
        listen(zoomEl, 'pointermove', function (e) {
          if (!dragging) return;
          var nx = e.clientX - dragStart.x;
          var ny = e.clientY - dragStart.y;
          if (Math.abs(nx - panX) > 2 || Math.abs(ny - panY) > 2) movedDuringDrag = true;
          panX = nx; panY = ny;
          applyTransform();
        });
        var endDrag = function () {
          if (!dragging) return;
          dragging = false;
          lb.classList.remove('is-panning');
        };
        listen(zoomEl, 'pointerup', endDrag);
        listen(zoomEl, 'pointercancel', endDrag);
        listen(zoomEl, 'pointerleave', endDrag);
      }

      // Open lightbox when clicking the main gallery image
      listen(mainImg, 'click', function () {
        var allThumbs = Array.from(thumbsContainer.querySelectorAll('.gh-pdp-gallery__thumb'));
        var activeIdx = Math.max(0, allThumbs.findIndex(function (t) { return t.classList.contains('is-active'); }));
        open(activeIdx);
      });
      listen(triggerEl, 'keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        if (mainImg.hidden) return;
        event.preventDefault();
        var allThumbs = Array.from(thumbsContainer.querySelectorAll('.gh-pdp-gallery__thumb'));
        var activeIdx = Math.max(0, allThumbs.findIndex(function (t) { return t.classList.contains('is-active'); }));
        open(activeIdx);
      });

      // Lightbox controls
      var prevBtn = lb.querySelector('[data-lb-prev]');
      var nextBtn = lb.querySelector('[data-lb-next]');
      var closeBtn = lb.querySelector('[data-lb-close]');
      listen(prevBtn, 'click', function () { step(-1); });
      listen(nextBtn, 'click', function () { step(1); });
      listen(closeBtn, 'click', function () { close(true); });

      listen(thumbsRail, 'click', function (event) {
        var thumbButton = event.target.closest && event.target.closest('.gh-lightbox__thumb');
        if (!thumbButton || !thumbsRail.contains(thumbButton)) return;
        var nextIndex = parseInt(thumbButton.dataset.lbIndex, 10);
        if (Number.isNaN(nextIndex)) return;
        idx = nextIndex;
        render();
      });

      listen(lb, 'click', function (e) {
        if (e.target === lb) close(true);
      });

      listen(document, 'keydown', function (e) {
        if (!lb.classList.contains('is-open')) return;
        if (e.key === 'Escape') close(true);
        else if (e.key === 'ArrowLeft') step(-1);
        else if (e.key === 'ArrowRight') step(1);
      });

      // Touch swipe on stage (disabled when zoomed so pan can work)
      if (stageEl) {
        var sx = 0, sy = 0, tracking = false;
        listen(stageEl, 'touchstart', function (e) {
          if (zoomed) return;
          if (!e.touches[0]) return;
          sx = e.touches[0].clientX; sy = e.touches[0].clientY; tracking = true;
        }, { passive: true });
        listen(stageEl, 'touchend', function (e) {
          if (zoomed || !tracking) return;
          tracking = false;
          var t = e.changedTouches[0];
          if (!t) return;
          var dx = t.clientX - sx, dy = t.clientY - sy;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
            step(dx < 0 ? 1 : -1);
          }
        });
      }

      function destroyPdpLightbox() {
        if (destroyed) return;
        destroyed = true;
        close(false);
        listeners.splice(0).forEach(function (removeListener) { removeListener(); });
        if (lbInitiallyInert) lb.setAttribute('inert', '');
        else lb.removeAttribute('inert');
        gallery.removeAttribute('data-pdp-lightbox-init');
      }

      listen(document, 'shopify:section:unload', function (event) {
        var section = event.target;
        if (section && typeof section.contains === 'function' && (section.contains(gallery) || section.contains(lb))) {
          destroyPdpLightbox();
        }
      });
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Roadmap tabs (gh-roadmap) — mobile/tablet V1/V2/V3 switcher.
  //   Tabs [data-rm-tab="<block.id>"] toggle matching
  //   cards [data-rm-card="<block.id>"] via .is-active.
  //   Desktop (1200px+) hides tabs and shows all cards via CSS — the
  //   .is-active toggle is harmless there.
  //   Double-init guard: dataset.rmInit on the tab list.
  // ─────────────────────────────────────────────────────────────
  // Activate a specific tab + matching card within a roadmap section.
  // Extracted so both the click handler and hash-routing can call it.
  function activateRoadmapTab(tabList, cards, tab) {
    var targetId = tab.getAttribute('data-rm-tab');
    tabList.querySelectorAll('.gh-roadmap__tab').forEach(function (t) {
      var on = t === tab;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    cards.forEach(function (c) {
      c.classList.toggle('is-active', c.getAttribute('data-rm-card') === targetId);
    });
  }

  function initRoadmapTabs(root) {
    var sections = (root || document).querySelectorAll('.gh-roadmap');
    sections.forEach(function (section) {
      var tabList = section.querySelector('.gh-roadmap__tabs');
      if (!tabList || tabList.dataset.rmInit) return;
      tabList.dataset.rmInit = '1';
      var cards = section.querySelectorAll('.gh-roadmap-card');

      tabList.addEventListener('click', function (e) {
        var tab = e.target.closest('.gh-roadmap__tab');
        if (!tab) return;
        activateRoadmapTab(tabList, cards, tab);
      });
    });

    // Hash-based deep-link: #roadmap or #roadmap-v2 / #roadmap-v3 etc.
    // Only runs when root is document (boot() call), not on section:load re-init.
    if (root && root !== document) return;

    var hash = window.location.hash; // e.g. "#roadmap-v2"
    var match = hash.match(/^#roadmap(?:-v(\d+))?$/);
    if (!match) return;

    var section = document.querySelector('.gh-roadmap');
    if (!section) return;

    var behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth';

    requestAnimationFrame(function () {
      section.scrollIntoView({ behavior: behavior, block: 'start' });

      // If a version number was captured (e.g. "2" from #roadmap-v2), activate that tab.
      var versionNum = match[1]; // undefined for bare #roadmap
      if (versionNum) {
        var targetTab = section.querySelector('.gh-roadmap__tab[data-rm-version="v' + versionNum + '"]');
        if (targetTab) {
          var tabList = section.querySelector('.gh-roadmap__tabs');
          var cards = section.querySelectorAll('.gh-roadmap-card');
          activateRoadmapTab(tabList, cards, targetTab);
        }
      }
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Header V2 — drawer (mobile) + Products dropdown (desktop)
  //
  // Drawer:
  //   - Hamburger [data-drawer-toggle] → opens #gh-drawer + #gh-drawer-backdrop
  //   - #gh-drawer-close, backdrop click, ESC → closes
  //   - #gh-drawer-products-toggle → toggles .gh-drawer__sub.is-open
  //   - body overflow:hidden while open
  // Dropdown:
  //   - JS-controlled hover with 180ms close delay (.is-open class)
  //   - CSS :hover retained as fallback if JS is unavailable
  //   - mouseenter clears any pending close timer (prevents flicker)
  // Idempotent via dataset.h2Init guard.
  // ─────────────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────
  // Language picker — PC nav flag dropdown
  // Source: Goehners_Mobile_v13.html ~line 23428 (lang-picker IIFE)
  // Manages open/close, aria-expanded, outside-click and Esc.
  // Locale change is a native form submit — JS only handles UI state.
  // Double-init guard: data-lp-init on the trigger button.
  // ─────────────────────────────────────────────────────────────
  function initLangPicker(root) {
    root = root || document;
    var picker = (root === document)
      ? document.getElementById('lang-picker')
      : root.querySelector('#lang-picker');
    if (!picker) return;
    var btn = picker.querySelector('[data-lang-toggle]');
    if (!btn || btn.dataset.lpInit) return;
    btn.dataset.lpInit = '1';

    var menu = picker.querySelector('.gh-nav__lang-menu');
    if (!menu) return;
    var items = Array.from(menu.querySelectorAll('.gh-nav__lang-item'));
    if (!items.length) return;

    function currentItemIndex() {
      var focusedIndex = items.indexOf(document.activeElement);
      if (focusedIndex >= 0) return focusedIndex;
      var currentIndex = items.findIndex(function (item) { return item.classList.contains('is-current'); });
      return currentIndex >= 0 ? currentIndex : 0;
    }

    function focusItem(index) {
      var normalized = (index + items.length) % items.length;
      items.forEach(function (item, itemIndex) {
        item.setAttribute('tabindex', itemIndex === normalized ? '0' : '-1');
      });
      items[normalized].focus();
    }

    function setOpen(open, focusIndex) {
      picker.classList.toggle('is-open', open);
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      menu.setAttribute('aria-hidden', open ? 'false' : 'true');
      if (open && typeof focusIndex === 'number') focusItem(focusIndex);
    }

    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      var opening = !picker.classList.contains('is-open');
      setOpen(opening, opening ? currentItemIndex() : undefined);
    });

    btn.addEventListener('keydown', function (event) {
      var index = currentItemIndex();
      if (event.key === 'ArrowDown') index = (index + 1) % items.length;
      else if (event.key === 'ArrowUp') index = (index - 1 + items.length) % items.length;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = items.length - 1;
      else return;
      event.preventDefault();
      setOpen(true, index);
    });

    menu.addEventListener('keydown', function (event) {
      var index = currentItemIndex();
      if (event.key === 'ArrowDown') index = (index + 1) % items.length;
      else if (event.key === 'ArrowUp') index = (index - 1 + items.length) % items.length;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = items.length - 1;
      else if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        btn.focus();
        return;
      } else if (event.key === 'Tab') {
        setOpen(false);
        return;
      } else return;
      event.preventDefault();
      focusItem(index);
    });

    // Each item is a submit button — clicking it submits the form and
    // reloads the page, so we just close the dropdown as a courtesy
    // in case something prevents the reload.
    menu.addEventListener('click', function (e) {
      if (e.target.closest('.gh-nav__lang-item')) {
        setOpen(false);
      }
    });

    document.addEventListener('click', function (e) {
      if (!picker.contains(e.target)) setOpen(false);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && picker.classList.contains('is-open')) {
        setOpen(false);
        btn.focus();
      }
    });
  }

  function initHeaderV2(root) {
    root = root || document;
    var hamburger = root.querySelector('[data-drawer-toggle]');
    if (!hamburger || hamburger.dataset.h2Init) return;
    var drawer = document.getElementById('gh-drawer');
    if (!drawer) return;
    hamburger.dataset.h2Init = '1';

    var backdrop = document.getElementById('gh-drawer-backdrop');
    var closeBtn = document.getElementById('gh-drawer-close');
    var productsToggle = document.getElementById('gh-drawer-products-toggle');
    var productsSub = document.getElementById('gh-drawer-products-sub');
    var previousBodyOverflow = '';
    var ownsBodyOverflow = false;
    var ownsBodyOpenClass = false;
    var drawerInitiallyInert = drawer.hasAttribute('inert');
    var listeners = [];
    var destroyed = false;
    var ddCloseTimer = null;

    function listen(target, type, handler, options) {
      if (!target) return;
      target.addEventListener(type, handler, options);
      listeners.push(function () { target.removeEventListener(type, handler, options); });
    }

    function openDrawer() {
      if (destroyed || drawer.classList.contains('is-open')) return;
      previousBodyOverflow = document.body.style.overflow;
      ownsBodyOverflow = true;
      ownsBodyOpenClass = !document.body.classList.contains('gh-drawer-open');
      drawer.removeAttribute('inert');
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      if (backdrop) backdrop.classList.add('is-open');
      hamburger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('gh-drawer-open');
      document.body.style.overflow = 'hidden';
      if (typeof trapFocus === 'function') trapFocus(drawer, closeBtn || drawer);
      else if (closeBtn) closeBtn.focus();
    }

    function closeDrawer(restoreFocus) {
      var wasOpen = drawer.classList.contains('is-open');
      if (wasOpen) {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        drawer.setAttribute('inert', '');
      }
      if (backdrop) backdrop.classList.remove('is-open');
      hamburger.setAttribute('aria-expanded', 'false');
      if (ownsBodyOpenClass) document.body.classList.remove('gh-drawer-open');
      ownsBodyOpenClass = false;
      if (ownsBodyOverflow) document.body.style.overflow = previousBodyOverflow;
      ownsBodyOverflow = false;
      if (wasOpen && typeof removeTrapFocus === 'function') {
        removeTrapFocus(restoreFocus === false ? null : hamburger, drawer);
      } else if (wasOpen && restoreFocus !== false && hamburger.isConnected) {
        hamburger.focus();
      }
    }

    listen(hamburger, 'click', openDrawer);
    listen(closeBtn, 'click', function () { closeDrawer(true); });
    listen(backdrop, 'click', function () { closeDrawer(true); });

    listen(document, 'keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeDrawer(true);
      }
    });

    listen(window, 'resize', function () {
      if (window.innerWidth >= 1024 && drawer.classList.contains('is-open')) {
        closeDrawer(false);
      }
    });

    if (productsToggle && productsSub) {
      listen(productsToggle, 'click', function (e) {
        e.preventDefault();
        productsSub.classList.toggle('is-open');
        var expanded = productsSub.classList.contains('is-open');
        productsToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    }

    // ── Desktop dropdown hover-delay (desktop only, ≥1024px) ──
    // Adds .is-open to .gh-nav__dd on mouseenter (immediate open),
    // removes it after 180ms on mouseleave (clearTimeout prevents flicker
    // when the cursor briefly passes through the 10px ::before bridge).
    // CSS :hover rules are kept as a fallback if JS is unavailable.
    var ddItem = root.querySelector('.gh-nav__dd');
    if (ddItem) {
      listen(ddItem, 'mouseenter', function () {
        if (window.innerWidth < 1024) return;
        if (ddCloseTimer) {
          clearTimeout(ddCloseTimer);
          ddCloseTimer = null;
        }
        ddItem.classList.add('is-open');
        var panel = ddItem.querySelector('.gh-nav__dd-panel');
        if (panel) panel.setAttribute('aria-hidden', 'false');
      });

      listen(ddItem, 'mouseleave', function () {
        if (window.innerWidth < 1024) return;
        ddCloseTimer = setTimeout(function () {
          ddItem.classList.remove('is-open');
          var panel = ddItem.querySelector('.gh-nav__dd-panel');
          if (panel) panel.setAttribute('aria-hidden', 'true');
          ddCloseTimer = null;
        }, 180);
      });
    }

    function destroyHeaderV2() {
      if (destroyed) return;
      destroyed = true;
      closeDrawer(false);
      if (ddCloseTimer) clearTimeout(ddCloseTimer);
      listeners.splice(0).forEach(function (removeListener) { removeListener(); });
      if (drawerInitiallyInert) drawer.setAttribute('inert', '');
      else drawer.removeAttribute('inert');
      delete hamburger.dataset.h2Init;
    }

    listen(document, 'shopify:section:unload', function (event) {
      var section = event.target;
      if (section && typeof section.contains === 'function' && (section.contains(hamburger) || section.contains(drawer))) {
        destroyHeaderV2();
      }
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Order Status — tracking number copy button
  // Used by: custom-page-order-status.liquid
  // Double-init guard: data-os-init on the copy button
  // ─────────────────────────────────────────────────────────────
  function initPageOrderStatus() {
    var copyBtn = document.getElementById('gh-os-tracking-copy');
    if (!copyBtn || copyBtn.dataset.osInit) return;
    copyBtn.dataset.osInit = '1';

    copyBtn.addEventListener('click', function () {
      var tn = copyBtn.getAttribute('data-tn-copy') || '';
      if (!tn) return;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(tn).then(function () {
          showCopyFeedback(copyBtn);
        }).catch(function () {
          fallbackCopy(tn, copyBtn);
        });
      } else {
        fallbackCopy(tn, copyBtn);
      }
    });

    function fallbackCopy(text, btn) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;top:-9999px;left:-9999px';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); showCopyFeedback(btn); } catch (e) {}
      document.body.removeChild(ta);
    }

    function showCopyFeedback(btn) {
      var orig = btn.innerHTML;
      btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="width:16px;height:16px;display:block"><path d="M4 12l5 5 11-11"/></svg>';
      btn.style.color = 'var(--teal)';
      setTimeout(function () {
        btn.innerHTML = orig;
        btn.style.color = '';
      }, 2000);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Thank You page — checkmark SVG draw animation on IntersectionObserver
  // Used by: custom-page-thank-you.liquid
  // Double-init guard: data-ty-init attribute on section root
  // ─────────────────────────────────────────────────────────────
  function initPageThankYou() {
    var section = document.getElementById('gh-thank-you-section');
    if (!section || section.dataset.tyAnimInit) return;
    section.dataset.tyAnimInit = '1';

    var checkEl = section.querySelector('[data-ty-check]');
    if (!checkEl) return;

    function drawCheck() {
      checkEl.classList.add('is-drawn');
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            // Small delay so the page layout settles before animating
            setTimeout(drawCheck, 120);
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0.3 });
      io.observe(checkEl);
    } else {
      // Fallback: draw immediately
      drawCheck();
    }
  }

  // ─────────────────────────────────────────────────────────────
  // PDP Demo Video play button (custom-pdp-demo.liquid)
  //
  // Shows a centred overlay play button before the video starts.
  // Clicking it: adds .is-played to .gh-pdp-video (hides the button via CSS),
  // unmutes and plays the video. Idempotent via data-pdp-demo-init guard.
  // ─────────────────────────────────────────────────────────────
  function startPdpDemoVideo(wrap, video) {
    wrap.classList.add('is-played');
    video.muted = false;
    var playPromise = video.play();
    if (playPromise && playPromise.catch) {
      playPromise.catch(function () {
        wrap.classList.remove('is-played');
      });
    }
    return playPromise;
  }

  function initPdpDemoVideo(root) {
    root = root || document;
    root.querySelectorAll('.gh-pdp-video:not([data-pdp-demo-init])').forEach(function (wrap) {
      var video = wrap.querySelector('video');
      var btn = wrap.querySelector('.gh-pdp-video__play');
      if (!video || !btn) return;
      wrap.setAttribute('data-pdp-demo-init', '');

      function loadPoster() {
        var poster = video.dataset.poster;
        if (!poster) return;
        video.poster = poster;
        video.removeAttribute('data-poster');
      }

      if ('IntersectionObserver' in window && video.dataset.poster) {
        var posterObserver = new IntersectionObserver(function (entries) {
          if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
          loadPoster();
          posterObserver.disconnect();
        }, { rootMargin: '400px 0px', threshold: 0 });
        posterObserver.observe(wrap);
      } else {
        loadPoster();
      }

      btn.addEventListener('click', function () {
        loadPoster();
        startPdpDemoVideo(wrap, video);
      });
    });
  }

  // Reallocate desktop proof space without changing the original section footprint.
  function initPdpFullRunLayout(section, player) {
    var inner = section.querySelector('.gh-pdp-mod__inner');
    if (!inner) return;
    var frame = 0;
    var disposed = false;

    function reset() {
      section.classList.remove('is-desktop-reflow');
      section.style.removeProperty('--full-run-section-height');
      section.style.removeProperty('--full-run-player-width');
    }

    function measure() {
      frame = 0;
      reset();
      if (!section.isConnected) { cleanup(); return; }
      if (!window.matchMedia('(min-width: 1024px)').matches) return;
      var originalHeight = section.getBoundingClientRect().height;
      if (!originalHeight) return;

      section.classList.add('is-desktop-reflow');
      // Measure actual flow, including collapsed margins and translated line wraps.
      var naturalHeight = section.getBoundingClientRect().height;
      var playerHeight = player.getBoundingClientRect().height;
      var availableHeight = originalHeight - (naturalHeight - playerHeight);
      if (availableHeight < 200) { reset(); return; }
      var playerWidth = Math.min(inner.clientWidth, availableHeight * 16 / 9);
      section.style.setProperty('--full-run-player-width', playerWidth + 'px');
      section.style.setProperty('--full-run-section-height', originalHeight + 'px');

      // Fall back to the unchanged layout if an unexpected embed/style cannot fit.
      var sectionStyle = window.getComputedStyle(section);
      var contentBottom = section.getBoundingClientRect().bottom -
        parseFloat(sectionStyle.paddingBottom) - parseFloat(sectionStyle.borderBottomWidth);
      if (inner.getBoundingClientRect().bottom > contentBottom + 1) reset();
    }

    function schedule() {
      if (disposed || frame) return;
      frame = window.requestAnimationFrame(measure);
    }

    function onUnload(event) {
      if (event.target === section || event.target.contains(section)) cleanup();
    }

    function cleanup() {
      disposed = true;
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('shopify:section:unload', onUnload);
      reset();
    }

    window.addEventListener('resize', schedule);
    document.addEventListener('shopify:section:unload', onUnload);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
    schedule();
    return cleanup;
  }

  // Full-run proof: native YouTube preview is lazy-loaded without autoplay; optional chapter controls reuse it.
  function initPdpFullRun(root) {
    root = root || document;
    root.querySelectorAll('[data-full-run]:not([data-full-run-init])').forEach(function (section) {
      section.setAttribute('data-full-run-init', '');
      var videoId = section.getAttribute('data-video-id') || '';
      var player = section.querySelector('[data-full-run-player]');
      if (player) initPdpFullRunLayout(section, player);
      var controls = section.querySelectorAll('[data-full-run-time]');
      var valid = /^[A-Za-z0-9_-]{11}$/.test(videoId);
      controls.forEach(function (button) {
        button.disabled = !valid;
        if (!valid) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
      });
      if (!valid || !player) return;

      controls.forEach(function (button) {
        button.addEventListener('click', function () {
          var time = Number(button.getAttribute('data-full-run-time'));
          if (!Number.isFinite(time) || time < 0 || time > 1140) return;
          var iframe = player.querySelector('iframe');
          if (!iframe) {
            iframe = document.createElement('iframe');
            iframe.title = section.getAttribute('data-video-title') || 'SteamVex full-run video';
            iframe.setAttribute('allow', 'encrypted-media; picture-in-picture');
            iframe.setAttribute('allowfullscreen', '');
            iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
            player.replaceChildren(iframe);
          }
          iframe.src = 'https://www.youtube-nocookie.com/embed/' + videoId +
            '?playsinline=1&rel=0&hl=en&start=' + Math.floor(time);
          iframe.focus();
        });
      });
    });
  }

  // PDP demo chapter navigation: seek the section's own video and keep the
  // active chapter synchronized as playback advances. Idempotent for editor reloads.
  function initPdpVideoChapters(root) {
    root = root || document;
    root.querySelectorAll('[data-video-chapters]:not([data-video-chapters-init])').forEach(function (nav) {
      var section = nav.closest('.goehners-pdp-demo') || nav.parentElement;
      var wrap = section && section.querySelector('.gh-pdp-video');
      var video = wrap && wrap.querySelector('video');
      var chapters = Array.prototype.slice.call(nav.querySelectorAll('[data-video-time]'));
      if (!video || !chapters.length) return;

      nav.setAttribute('data-video-chapters-init', '');

      function setActive(activeButton) {
        chapters.forEach(function (button) {
          var active = button === activeButton;
          button.classList.toggle('is-active', active);
          if (active) button.setAttribute('aria-current', 'true');
          else button.removeAttribute('aria-current');
        });
      }

      chapters.forEach(function (button) {
        button.addEventListener('click', function () {
          var time = Number(button.getAttribute('data-video-time'));
          if (!Number.isFinite(time) || time < 0) return;
          video.currentTime = time;
          setActive(button);
          startPdpDemoVideo(wrap, video);
        });
      });

      video.addEventListener('timeupdate', function () {
        var active = chapters[0];
        chapters.forEach(function (button) {
          if (Number(button.getAttribute('data-video-time')) <= video.currentTime) active = button;
        });
        setActive(active);
      });
    });
  }

  // ─────────────────────────────────────────────────────────────
  // PDP Sticky Add-to-Cart bar (mobile, <1024px)
  // C3 — V13 source: Goehners_Mobile_v13.html lines 753-795, 21851-21857
  // Shows only after main buybox ATC [data-pdp-add-btn] has been scrolled
  // above the viewport. It stays hidden while the button is still below.
  // Delegates click to the real submit button so all cart-drawer / variant
  // logic runs identically. Price kept in sync via MutationObserver.
  // Double-init guard: data-psc-init on bar element.
  // ─────────────────────────────────────────────────────────────
  function initPdpStickyCta(root) {
    var bar = (root || document).querySelector('[data-pdp-sticky]');
    if (!bar) return;
    // Idempotent guard
    if (bar.dataset.pscInit) return;
    bar.dataset.pscInit = '1';

    // Initialize at every viewport width. CSS hides the bar at >=1024px,
    // while keeping the observer alive lets desktop-to-mobile resizes work
    // without requiring a page reload.

    // The real add-to-cart submit button inside the product form
    var mainBtn = document.querySelector('[data-pdp-add-btn]');
    if (!mainBtn) return;

    function setStickyVisibility(show) {
      show = show && window.matchMedia('(max-width: 1023px)').matches;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      bar.inert = !show;
      document.body.classList.toggle('has-sticky-cta', show);
    }

    // ── Show only after the main ATC has passed above the viewport ──
    // A throttled scroll check also handles jump-to-top navigation, where an
    // IntersectionObserver can move from above to below without intersecting.
    var stickyScrollPending = false;
    var syncStickyVisibility = function () {
      stickyScrollPending = false;
      var viewport = window.visualViewport;
      var viewportTop = viewport ? Math.max(0, viewport.offsetTop) : 0;
      var viewportLeft = viewport ? Math.max(0, viewport.offsetLeft) : 0;
      var viewportWidth = viewport ? viewport.width : document.documentElement.clientWidth;
      bar.style.setProperty('--pdp-sticky-top', viewportTop + 'px');
      bar.style.setProperty('--pdp-sticky-left', viewportLeft + 'px');
      bar.style.setProperty('--pdp-sticky-width', viewportWidth + 'px');
      setStickyVisibility(mainBtn.getBoundingClientRect().bottom <= viewportTop);
    };
    var requestStickySync = function () {
      if (stickyScrollPending) return;
      stickyScrollPending = true;
      window.requestAnimationFrame(syncStickyVisibility);
    };
    window.addEventListener('scroll', requestStickySync, { passive: true });
    window.addEventListener('resize', requestStickySync);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', requestStickySync, { passive: true });
      window.visualViewport.addEventListener('scroll', requestStickySync, { passive: true });
    }
    syncStickyVisibility();

    // ── Delegate click to real ATC button ──
    var stickyAddBtn = bar.querySelector('[data-pdp-sticky-add]');
    if (stickyAddBtn) {
      stickyAddBtn.addEventListener('click', function () {
        if (!mainBtn.disabled) {
          mainBtn.click();
        }
      });
    }

    // ── Price sync: mirror [data-pdp-price] text into [data-pdp-sticky-price] ──
    var priceEl = document.querySelector('[data-pdp-price]');
    var stickyPriceEl = bar.querySelector('[data-pdp-sticky-price]');
    if (priceEl && stickyPriceEl && 'MutationObserver' in window) {
      var mo = new MutationObserver(function () {
        stickyPriceEl.textContent = priceEl.textContent.trim();
      });
      mo.observe(priceEl, { childList: true, characterData: true, subtree: true });
    }
  }

  // ─────────────────────────────────────────────────────────────
  // PDP module folds — vertical progressive enhancement only.
  // Without JS, every marked module remains fully visible.
  // ─────────────────────────────────────────────────────────────
  var pdpFoldFallbackId = 0;
  var pdpFoldResizeTimer = null;

  function refreshPdpModuleFolds(root) {
    root = root || document;
    var groups = root.querySelectorAll('[data-pdp-fold-ready]');

    groups.forEach(function (group) {
      if (typeof group.pdpFoldRefresh === 'function') {
        group.pdpFoldRefresh();
      }
    });
  }

  function initPdpModuleFolds(root) {
    root = root || document;
    var groups = root.querySelectorAll('[data-pdp-fold]:not([data-pdp-fold-ready])');

    groups.forEach(function (group) {
      group.setAttribute('data-pdp-fold-ready', 'true');

      if (!group.id) {
        pdpFoldFallbackId += 1;
        group.id = 'PdpFold-' + pdpFoldFallbackId;
      }

      var moreLabel = group.getAttribute('data-pdp-fold-more') || 'Show more';
      var lessLabel = group.getAttribute('data-pdp-fold-less') || 'Show less';
      var items = Array.prototype.slice.call(group.children);
      var section = group.closest('section');
      var heading = section ? section.querySelector('h2, h3') : null;
      var moduleName = heading ? heading.textContent.replace(/\s+/g, ' ').trim() : 'product details';
      var expanded = false;
      var foldable = false;

      var control = document.createElement('div');
      control.className = 'gh-pdp-fold__toggle';
      control.hidden = true;

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'gh-pdp-fold__button';
      button.setAttribute('aria-controls', group.id);

      var label = document.createElement('span');
      var icon = document.createElement('span');
      icon.className = 'gh-pdp-fold__icon';
      icon.setAttribute('aria-hidden', 'true');
      button.appendChild(label);
      button.appendChild(icon);
      control.appendChild(button);
      group.insertAdjacentElement('afterend', control);

      function setExpanded(next) {
        expanded = foldable && next;
        group.classList.toggle('is-pdp-fold-collapsed', foldable && !expanded);
        label.textContent = expanded ? lessLabel : moreLabel;
        button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        button.setAttribute(
          'aria-label',
          (expanded ? lessLabel : moreLabel) + ': ' + moduleName
        );
      }

      function syncOverflow() {
        var foldHeight = parseFloat(
          window.getComputedStyle(group).getPropertyValue('--pdp-fold-height')
        );

        foldable = !isNaN(foldHeight) && group.scrollHeight > foldHeight + 1;
        control.hidden = !foldable;
        setExpanded(foldable && expanded);
      }

      group.pdpFoldRefresh = syncOverflow;

      button.addEventListener('click', function () {
        var next = !expanded;

        if (next) {
          var clippedBottom = group.getBoundingClientRect().bottom;
          var firstRevealed = items.find(function (item) {
            return item.getBoundingClientRect().bottom > clippedBottom + 2;
          });

          setExpanded(true);
          if (!firstRevealed) return;

          var previousTabindex = firstRevealed.getAttribute('tabindex');
          firstRevealed.setAttribute('data-pdp-fold-focus', 'true');
          firstRevealed.setAttribute('tabindex', '-1');
          firstRevealed.focus({ preventScroll: true });
          firstRevealed.addEventListener('blur', function () {
            firstRevealed.removeAttribute('data-pdp-fold-focus');
            if (previousTabindex === null) {
              firstRevealed.removeAttribute('tabindex');
            } else {
              firstRevealed.setAttribute('tabindex', previousTabindex);
            }
          }, { once: true });
        } else {
          setExpanded(false);
          requestAnimationFrame(function () {
            button.focus({ preventScroll: true });
            var rect = button.getBoundingClientRect();

            if (rect.top < 80 || rect.bottom > window.innerHeight - 24) {
              var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
              button.scrollIntoView({
                behavior: reduceMotion ? 'auto' : 'smooth',
                block: 'center'
              });
            }
          });
        }
      });

      group.addEventListener('focusin', function (event) {
        if (foldable && !expanded && event.target !== group) {
          setExpanded(true);
        }
      });

      syncOverflow();
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () {
          if (group.isConnected) syncOverflow();
        });
      }
    });
  }

  window.addEventListener('resize', function () {
    window.clearTimeout(pdpFoldResizeTimer);
    pdpFoldResizeTimer = window.setTimeout(function () {
      refreshPdpModuleFolds(document);
    }, 120);
  });

  // Judge.me can retain an older first-page payload in its product metafield
  // after the widget's reviews-per-page setting changes. When that happens,
  // ask Judge.me to reload the current sort once so its own pagination API
  // renders the configured page size. No reviews are hidden or removed.
  function initJudgeMeMediaFallback(root) {
    root = root || document;
    var widget = root.querySelector('#judgeme_product_reviews[data-product-id]');
    if (!widget || widget.dataset.ghMediaFallbackInit) return;
    widget.dataset.ghMediaFallbackInit = '1';

    function hideFailedMedia(scope) {
      (scope || widget).querySelectorAll('video, img').forEach(function (media) {
        if (media.dataset.ghMediaErrorInit) return;
        media.dataset.ghMediaErrorInit = '1';
        media.addEventListener('error', function () {
          var region = media.closest('.jdgm-rev__pic-link, .jdgm-rev__video, .jdgm-rev__media, .jdgm-gallery__thumbnail') || media;
          region.hidden = true;
        }, { once: true });
      });
    }

    hideFailedMedia(widget);
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) hideFailedMedia(node);
        });
      });
    }).observe(widget, { childList: true, subtree: true });
  }

  function initJudgeMePaginationSync(root) {
    root = root || document;
    var widget = root.querySelector('#judgeme_product_reviews[data-product-id]');
    if (!widget || widget.dataset.ghPaginationSync) return;

    widget.dataset.ghPaginationSync = 'watching';
    var observer;
    var timeoutId;

    function finish(state) {
      widget.dataset.ghPaginationSync = state;
      if (observer) observer.disconnect();
      window.clearTimeout(timeoutId);
    }

    function syncPageSize() {
      var settings = window.jdgmSettings || {};
      var target = parseInt(settings.product_review_widget_per_page, 10);
      var reviews = widget.querySelectorAll('.jm-review-item');
      var sortSelect = widget.querySelector('.jdgm-sort-dropdown');

      if (!target || target < 1 || !reviews.length || !sortSelect) return false;
      if (reviews.length <= target) return false;

      sortSelect.dispatchEvent(new Event('change', { bubbles: true }));
      finish('refreshed');
      return true;
    }

    observer = new MutationObserver(syncPageSize);
    observer.observe(widget, { childList: true, subtree: true });
    syncPageSize();
    timeoutId = window.setTimeout(function () {
      if (widget.dataset.ghPaginationSync === 'watching') finish('ready');
    }, 15000);
  }

  // Theme-only presentation: retain Judge.me's own navigation controls and
  // product-card handlers. Reviews, page size and product associations stay native.
  function applyJudgeMePresentation(widget) {
    var locale = ((document.documentElement.lang || 'en').split('-')[0] || 'en').toLowerCase();
    var pageLabels = {
      en: 'Page [current] of [total]',
      fr: 'Page [current] sur [total]',
      de: 'Seite [current] von [total]',
      es: 'Página [current] de [total]'
    };

    widget.querySelectorAll('.jm-pagination-controls').forEach(function (controls) {
      var pages = Array.prototype.slice.call(controls.querySelectorAll('.jm-pagination-controls__button--page'));
      var navigation = controls.querySelectorAll('.jm-pagination-controls__button--nav');
      var currentButtons = pages.filter(function (button) {
        return button.getAttribute('aria-current') === 'page';
      });
      var numbers = pages.map(function (button) {
        var text = button.textContent.trim();
        return /^[1-9]\d*$/.test(text) ? Number(text) : NaN;
      });
      var current = currentButtons.length === 1 ? Number(currentButtons[0].textContent.trim()) : NaN;
      var total = numbers.length ? numbers[numbers.length - 1] : NaN;
      // The native widget includes the first and final page among its numbered
      // buttons, even around an ellipsis. Fall back if this contract changes.
      var valid = navigation.length === 2 && numbers.length > 1 && numbers[0] === 1 &&
        numbers.every(function (number, index) {
          return Number.isSafeInteger(number) && number > 0 && (!index || number > numbers[index - 1]);
        }) && Number.isSafeInteger(current) && current >= 1 && current <= total;
      var status = controls.querySelector('.gh-review-pagination__status');
      controls.classList.toggle('gh-review-pagination--compact', valid);
      if (!valid) {
        if (status) status.remove();
        return;
      }
      if (!status) {
        status = document.createElement('span');
        status.className = 'gh-review-pagination__status';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        status.setAttribute('aria-atomic', 'true');
        navigation[1].insertAdjacentElement('beforebegin', status);
      }
      var visible = String(current);
      var label = (pageLabels[locale] || pageLabels.en)
        .replace('[current]', current).replace('[total]', total);
      if (status.textContent !== visible) status.textContent = visible;
      if (status.getAttribute('aria-label') !== label) status.setAttribute('aria-label', label);
    });

    widget.querySelectorAll('.jm-product-variant-card').forEach(function (card) {
      var title = card.querySelector('.jm-product-variant-card__product-link');
      var thumbnail = card.querySelector('.jm-product-variant-card__thumbnail');
      var image = thumbnail && thumbnail.querySelector('img');
      if (!title || !image || !/\bSteamVex\b/i.test(title.textContent)) return;
      var path;
      try { path = new URL(image.currentSrc || image.src, window.location.href).pathname; }
      catch (error) { return; }
      // This exact asset is the verified grouped black-product review card.
      // Unknown products/assets are deliberately left alone, including reviews
      // of accessories or any future differently associated product.
      if (!path.endsWith('/goehners-steamvex-slate-black-google-shopping-main-20260904.jpg')) return;
      var blackLabels = { en: 'Slate Black', fr: 'Noir ardoise', de: 'Schieferschwarz', es: 'Negro pizarra' };
      var blackDot = document.querySelector('.gh-pdp-buybox__color-dot--black');
      var blackColor = blackDot && blackDot.closest('.gh-pdp-buybox__color-name');
      var colorName = blackColor && blackColor.textContent.trim();
      var name = 'SteamVex — ' + (colorName || blackLabels[locale] || blackLabels.en);
      if (title.textContent !== name) title.textContent = name;
      if (image.alt !== name) image.alt = name;
      var metadata = getJudgeMeMetadataCopy();
      var viewLabel = metadata ? interpolateJudgeMeMetadata(metadata.viewProductPage, { title: name }) : 'View ' + name + ' product page';
      if (title.getAttribute('aria-label') !== viewLabel) title.setAttribute('aria-label', viewLabel);
      if (title.hasAttribute('title') && title.getAttribute('title') !== name) title.setAttribute('title', name);
      if (thumbnail.getAttribute('aria-label') !== viewLabel) thumbnail.setAttribute('aria-label', viewLabel);
    });
  }

  function initJudgeMePresentation(root) {
    var widget = (root || document).querySelector('#judgeme_product_reviews[data-product-id]');
    if (!widget || widget.dataset.ghPresentationInit) return;
    widget.dataset.ghPresentationInit = '1';
    var pending = false;
    function scheduleApply() {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () {
        pending = false;
        if (widget.isConnected) applyJudgeMePresentation(widget);
      });
    }
    applyJudgeMePresentation(widget);
    new MutationObserver(scheduleApply).observe(widget, {
      childList: true, subtree: true, characterData: true,
      attributes: true, attributeFilter: ['aria-current', 'src', 'srcset']
    });
  }

  // Judge.me's current widget bundles localize visible FR/DE/ES copy but leave
  // some accessibility metadata in English. Keep this repair strictly inside
  // Judge.me-owned DOM and strictly to aria-label/title/alt attributes.
  var judgeMeMetadataRootSelector = [
    '#judgeme_product_reviews',
    '.jdgm-review-widget-modal.jdgm-write-review-modal',
    '.jdgm-modal.jdgm-widget-revamp'
  ].join(',');

  var judgeMeMetadataExactRules = {
    'Close review form': 'closeReviewForm',
    'Review form pages': 'reviewFormPages',
    'Review form navigation': 'reviewFormNavigation',
    'Rating': 'rating',
    'Privacy and Terms': 'privacyAndTerms',
    'Previous step': 'previousStep',
    'Next step': 'nextStep',
    'Remove file': 'removeFile',
    'Thumbnail for 1 item': 'oneThumbnail',
    'Loading thumbnail': 'loadingThumbnail',
    'Loading image': 'loadingImage',
    'Scroll thumbnails left': 'scrollThumbnailsLeft',
    'Scroll thumbnails right': 'scrollThumbnailsRight',
    'View review image': 'viewReviewImage',
    'Play review video': 'playReviewVideo',
    'Review Image': 'reviewImage',
    'Video Thumbnail': 'videoThumbnail',
    'Pagination Navigation': 'paginationNavigation',
    'Go to previous page': 'goToPreviousPage',
    'Go to next page': 'goToNextPage',
    'Mark as helpful': 'markHelpful',
    'Mark as not helpful': 'markNotHelpful',
    'Pinned review': 'pinnedReview',
    'Choose a review picture (optional)': 'choosePictureOptional',
    'Choose a review video (optional)': 'chooseVideoOptional',
    'Choose a review picture/video (optional)': 'choosePictureVideoOptional',
    'Name format': 'nameFormat',
    'YouTube URL': 'youtubeUrl',
    'Review Highlight': 'reviewHighlight',
    'Tabs': 'tabs',
    'View reviews on the Reviews Site': 'viewReviewsSite',
    'Close modal': 'closeModal',
    'Customer reviews carousel': 'customerReviewsCarousel',
    'Review Media Gallery': 'reviewMediaGallery',
    'Lightbox': 'reviewMediaGallery'
  };

  var judgeMeMetadataGenericRules = {
    'Sort reviews by': {
      key: 'sortReviewsBy',
      selector: '.jdgm-sort-dropdown,[class*="review-sort"],[class*="sort-dropdown"]'
    },
    'Previous reviews': {
      key: 'previousReviews',
      selector: '.jdgm-carousel,[class*="review-carousel"],[class*="pagination"]'
    },
    'Next reviews': {
      key: 'nextReviews',
      selector: '.jdgm-carousel,[class*="review-carousel"],[class*="pagination"]'
    },
    'Previous': {
      key: 'previous',
      selector: '[class*="carousel"],[class*="lightbox"],[class*="thumbnail"],[role="dialog"]'
    },
    'Next': {
      key: 'next',
      selector: '[class*="carousel"],[class*="lightbox"],[class*="thumbnail"],[role="dialog"]'
    },
    'Search': {
      key: 'search',
      selector: '[class*="search"],[role="search"],input[type="search"]'
    },
    'Close': {
      key: 'close',
      selector: '[class*="modal"],[class*="lightbox"],[role="dialog"]'
    }
  };

  var judgeMeMetadataRequiredKeys = [
    'oneStar',
    'manyStars',
    'manyThumbnails',
    'thumbnailIndex',
    'goToItem',
    'goToPage',
    'ratingOutOfOne',
    'ratingOutOfMany',
    'viewProductPage',
    'shareOn'
  ].concat(
    Object.keys(judgeMeMetadataExactRules).map(function (source) {
      return judgeMeMetadataExactRules[source];
    }),
    Object.keys(judgeMeMetadataGenericRules).map(function (source) {
      return judgeMeMetadataGenericRules[source].key;
    })
  );

  function getJudgeMeMetadataCopy() {
    var locale = ((document.documentElement.lang || 'en').split('-')[0] || 'en').toLowerCase();
    if (['fr', 'de', 'es'].indexOf(locale) === -1) return null;

    var accessibilityStrings = window.accessibilityStrings;
    var copy = accessibilityStrings && accessibilityStrings.judgeMe;
    if (!copy) return null;

    var complete = judgeMeMetadataRequiredKeys.every(function (key) {
      return typeof copy[key] === 'string' && copy[key] !== '';
    });
    return complete ? copy : null;
  }

  function interpolateJudgeMeMetadata(template, values) {
    return Object.keys(values).reduce(function (result, key) {
      return result.replace('[' + key + ']', values[key]);
    }, template);
  }

  function matchesJudgeMeMetadataContext(node, selector) {
    return node.matches(selector) || !!node.closest(selector);
  }

  function localizeJudgeMeMetadataValue(node, value, copy) {
    if (Object.prototype.hasOwnProperty.call(judgeMeMetadataExactRules, value)) {
      return copy[judgeMeMetadataExactRules[value]];
    }

    if (Object.prototype.hasOwnProperty.call(judgeMeMetadataGenericRules, value)) {
      var genericRule = judgeMeMetadataGenericRules[value];
      return matchesJudgeMeMetadataContext(node, genericRule.selector) ? copy[genericRule.key] : value;
    }

    var match = value.match(/^Thumbnails for (\d+) items$/);
    if (match) {
      if (parseInt(match[1], 10) === 1) return copy.oneThumbnail;
      return interpolateJudgeMeMetadata(copy.manyThumbnails, { count: match[1] });
    }

    match = value.match(/^Go to item (\d+)$/);
    if (match) return interpolateJudgeMeMetadata(copy.goToItem, { index: match[1] });

    match = value.match(/^Thumbnail (\d+)$/);
    if (match) return interpolateJudgeMeMetadata(copy.thumbnailIndex, { index: match[1] });

    match = value.match(/^Go to page (\d+)$/);
    if (match) return interpolateJudgeMeMetadata(copy.goToPage, { index: match[1] });

    match = value.match(/^(\d+(?:\.\d+)?) out of (\d+(?:\.\d+)?) stars$/);
    if (match) {
      var ratingTemplate = parseFloat(match[1]) === 1 ? copy.ratingOutOfOne : copy.ratingOutOfMany;
      return interpolateJudgeMeMetadata(ratingTemplate, {
        rating: match[1],
        max: match[2]
      });
    }

    match = value.match(/^(\d+(?:\.\d+)?) stars?$/);
    if (match) {
      var starTemplate = parseFloat(match[1]) === 1 ? copy.oneStar : copy.manyStars;
      return interpolateJudgeMeMetadata(starTemplate, { count: match[1] });
    }

    match = value.match(/^View (.+) product page$/);
    if (match) return interpolateJudgeMeMetadata(copy.viewProductPage, { title: match[1] });

    match = value.match(/^Share on (.+)$/);
    if (match) return interpolateJudgeMeMetadata(copy.shareOn, { platform: match[1] });

    return value;
  }

  function isJudgeMeMetadataNode(node) {
    if (!node || node.nodeType !== 1) return false;
    return node.matches(judgeMeMetadataRootSelector) || !!node.closest(judgeMeMetadataRootSelector);
  }

  function applyJudgeMeLocalizedMetadata(root, copy) {
    if (!root || !copy || typeof root.querySelectorAll !== 'function') return;

    var nodes = [];
    if (root.nodeType === 1 && root.matches('[aria-label],[title],[alt]')) nodes.push(root);
    root.querySelectorAll('[aria-label],[title],[alt]').forEach(function (node) {
      nodes.push(node);
    });

    nodes.forEach(function (node) {
      if (!isJudgeMeMetadataNode(node)) return;
      ['aria-label', 'title', 'alt'].forEach(function (attribute) {
        if (!node.hasAttribute(attribute)) return;
        var current = node.getAttribute(attribute);
        var localized = localizeJudgeMeMetadataValue(node, current, copy);
        if (localized !== current) node.setAttribute(attribute, localized);
      });
    });
  }

  var judgeMeMetadataObservedRoots = new WeakSet();
  var judgeMeMetadataObserver = null;

  function observeJudgeMeMetadataRoot(root, copy) {
    applyJudgeMeLocalizedMetadata(root, copy);
    if (!judgeMeMetadataObserver || judgeMeMetadataObservedRoots.has(root)) return;

    judgeMeMetadataObserver.observe(root, {
      attributes: true,
      subtree: true,
      attributeFilter: ['aria-label', 'title', 'alt']
    });
    judgeMeMetadataObservedRoots.add(root);
  }

  function findJudgeMeMetadataRoots(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return [];
    var roots = [];
    if (root.nodeType === 1 && root.matches(judgeMeMetadataRootSelector)) roots.push(root);
    root.querySelectorAll(judgeMeMetadataRootSelector).forEach(function (candidate) {
      if (roots.indexOf(candidate) === -1) roots.push(candidate);
    });
    return roots;
  }

  function processJudgeMeMetadataSubtree(node, copy) {
    if (!node || node.nodeType !== 1) return;
    var containingRoot = node.closest(judgeMeMetadataRootSelector);
    if (containingRoot) {
      observeJudgeMeMetadataRoot(containingRoot, copy);
      applyJudgeMeLocalizedMetadata(node, copy);
      return;
    }
    findJudgeMeMetadataRoots(node).forEach(function (root) {
      observeJudgeMeMetadataRoot(root, copy);
    });
  }

  function initJudgeMeLocalizedMetadata(root) {
    var copy = getJudgeMeMetadataCopy();
    if (!copy) return;

    if (!judgeMeMetadataObserver && document.body) {
      judgeMeMetadataObserver = new MutationObserver(function (mutations) {
        var activeCopy = getJudgeMeMetadataCopy();
        if (!activeCopy) return;
        mutations.forEach(function (mutation) {
          if (mutation.type === 'attributes') {
            applyJudgeMeLocalizedMetadata(mutation.target, activeCopy);
            return;
          }
          mutation.addedNodes.forEach(function (node) {
            processJudgeMeMetadataSubtree(node, activeCopy);
          });
        });
      });
      judgeMeMetadataObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
      document._ghJudgeMeMetadataObserver = judgeMeMetadataObserver;
    }

    findJudgeMeMetadataRoots(root || document).forEach(function (judgeMeRoot) {
      observeJudgeMeMetadataRoot(judgeMeRoot, copy);
    });
  }

  var judgeMeVerifiedBuyerLabels = {
    en: 'Verified Buyer',
    fr: 'Acheteur vérifié',
    de: 'Verifizierter Käufer',
    es: 'Comprador verificado'
  };

  var judgeMeInitialCharacterPattern;
  try {
    judgeMeInitialCharacterPattern = new RegExp('[\\p{L}\\p{N}]', 'u');
  } catch (error) {
    judgeMeInitialCharacterPattern = /[A-Za-z0-9]/;
  }

  function getJudgeMeNameCharacters(value) {
    var normalized = String(value || '');
    if (typeof normalized.normalize === 'function') normalized = normalized.normalize('NFC');
    return Array.from(normalized).filter(function (character) {
      return judgeMeInitialCharacterPattern.test(character);
    });
  }

  function getJudgeMeReviewerInitials(name) {
    var parts = String(name || '').trim().split(/[\s._-]+/).map(function (part) {
      return getJudgeMeNameCharacters(part);
    }).filter(function (part) {
      return part.length > 0;
    });
    if (parts.length > 1) {
      return (parts[0][0] + parts[parts.length - 1][0]).toLocaleUpperCase();
    }
    var compact = parts[0] || getJudgeMeNameCharacters(name);
    return (compact.slice(0, 2).join('') || '?').toLocaleUpperCase();
  }

  function applyJudgeMeReviewerIdentity(widget) {
    var locale = ((document.documentElement.lang || 'en').split('-')[0] || 'en').toLowerCase();
    var verifiedLabel = judgeMeVerifiedBuyerLabels[locale] || judgeMeVerifiedBuyerLabels.en;

    widget.querySelectorAll('.jm-reviewer-info').forEach(function (info) {
      var name = info.querySelector('.jm-reviewer-info__name');
      var initial = info.querySelector('.jm-reviewer-avatar--with-initial .jm-reviewer-avatar__initial .jm-text');
      var avatar = info.querySelector('.jm-reviewer-avatar--with-initial');
      var badge = info.querySelector('.jm-verified-buyer-badge');
      var badgeText = badge && badge.querySelector('.jm-verified-buyer-badge__text');

      if (name && initial) {
        var initials = getJudgeMeReviewerInitials(name.textContent);
        if (initial.textContent !== initials) initial.textContent = initials;
      }
      if (avatar) avatar.setAttribute('aria-hidden', 'true');
      info.classList.toggle('gh-reviewer--verified', !!badge);
      if (badgeText && badgeText.textContent !== verifiedLabel) badgeText.textContent = verifiedLabel;
    });
  }

  function initJudgeMeReviewerIdentity(root) {
    root = root || document;
    var widget = root.querySelector('#judgeme_product_reviews[data-product-id]');
    if (!widget || widget.dataset.ghReviewerIdentityInit) return;

    widget.dataset.ghReviewerIdentityInit = '1';
    applyJudgeMeReviewerIdentity(widget);
    new MutationObserver(function () {
      applyJudgeMeReviewerIdentity(widget);
    }).observe(widget, { childList: true, subtree: true, characterData: true });
  }

  // The store owner confirmed these imported reviews originated on Amazon.
  // Match Judge.me's exact provider-import copy so other badge types stay untouched.
  var judgeMeAmazonSourceLabels = {
    en: {
      native: 'Review collected from another provider',
      approved: 'Originally posted on Amazon'
    },
    fr: {
      native: "Avis collecté d'un autre fournisseur",
      approved: 'Publié à l’origine sur Amazon'
    },
    de: {
      native: 'Bewertung gesammelt von einem anderen Anbieter',
      approved: 'Ursprünglich auf Amazon veröffentlicht'
    },
    es: {
      native: 'Reseña recopilada de otro proveedor',
      approved: 'Publicado originalmente en Amazon'
    }
  };

  function applyJudgeMeAmazonSourceLabels(widget) {
    var locale = ((document.documentElement.lang || 'en').split('-')[0] || 'en').toLowerCase();
    var copy = judgeMeAmazonSourceLabels[locale] || judgeMeAmazonSourceLabels.en;
    var labels = widget.querySelectorAll('.jm-transparency-badge-list .jm-text');

    labels.forEach(function (label) {
      var current = (label.textContent || '').trim();
      if (label.dataset.ghAmazonSource !== '1' && current !== copy.native) return;

      label.dataset.ghAmazonSource = '1';
      if (current !== copy.approved) label.textContent = copy.approved;
    });
  }

  function initJudgeMeAmazonSourceLabels(root) {
    root = root || document;
    var widget = root.querySelector('#judgeme_product_reviews[data-product-id]');
    if (!widget || widget.dataset.ghAmazonSourceInit) return;

    widget.dataset.ghAmazonSourceInit = '1';
    applyJudgeMeAmazonSourceLabels(widget);
    new MutationObserver(function () {
      applyJudgeMeAmazonSourceLabels(widget);
    }).observe(widget, { childList: true, subtree: true, characterData: true });
  }

  // Long Judge.me reviews collapse independently while reviewer details,
  // source badges, and review media stay visible. Judge.me replaces review
  // nodes after sorting/pagination, so an idempotent observer re-applies the
  // control to newly rendered bodies.
  var judgeMeReviewFoldId = 0;
  var judgeMeReviewFoldLabels = {
    en: { more: 'Read more', less: 'Show less' },
    fr: { more: 'Lire la suite', less: 'Afficher moins' },
    de: { more: 'Mehr lesen', less: 'Weniger anzeigen' },
    es: { more: 'Leer más', less: 'Mostrar menos' }
  };

  function applyJudgeMeReviewFolds(widget) {
    var locale = ((document.documentElement.lang || 'en').split('-')[0] || 'en').toLowerCase();
    var labels = judgeMeReviewFoldLabels[locale] || judgeMeReviewFoldLabels.en;
    var bodies = widget.querySelectorAll('.jm-review-body-wrapper:not([data-gh-review-fold])');

    bodies.forEach(function (body) {
      body.dataset.ghReviewFold = '1';
      body.classList.add('gh-review-fold__body');

      var foldHeight = parseFloat(
        window.getComputedStyle(body).getPropertyValue('--gh-review-fold-height')
      );
      if (isNaN(foldHeight) || body.scrollHeight <= foldHeight + 4) return;

      judgeMeReviewFoldId += 1;
      if (!body.id) body.id = 'GhReviewFold-' + judgeMeReviewFoldId;

      var card = body.closest('.jm-review-item');
      var reviewer = card ? card.querySelector('.jm-reviewer-info__name') : null;
      var reviewerName = reviewer ? reviewer.textContent.trim() : 'review';
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'gh-review-fold__button';
      button.setAttribute('aria-controls', body.id);

      function setExpanded(expanded) {
        body.classList.toggle('is-collapsed', !expanded);
        button.textContent = expanded ? labels.less : labels.more;
        button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        button.setAttribute(
          'aria-label',
          (expanded ? labels.less : labels.more) + ': ' + reviewerName
        );
      }

      button.addEventListener('click', function () {
        var expanded = button.getAttribute('aria-expanded') === 'true';
        setExpanded(!expanded);

        if (expanded) {
          requestAnimationFrame(function () {
            var cardRect = card ? card.getBoundingClientRect() : null;
            if (cardRect && cardRect.top < 80) {
              var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
              card.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
            }
          });
        }
      });

      body.insertAdjacentElement('afterend', button);
      setExpanded(false);
    });
  }

  function initJudgeMeReviewFolds(root) {
    root = root || document;
    var widget = root.querySelector('#judgeme_product_reviews[data-product-id]');
    if (!widget || widget.dataset.ghReviewFoldInit) return;

    widget.dataset.ghReviewFoldInit = '1';
    var framePending = false;
    function scheduleApply() {
      if (framePending) return;
      framePending = true;
      requestAnimationFrame(function () {
        framePending = false;
        applyJudgeMeReviewFolds(widget);
      });
    }

    applyJudgeMeReviewFolds(widget);
    new MutationObserver(scheduleApply).observe(widget, { childList: true, subtree: true });
  }

  // Klaviyo form UI — localize app-owned controls when popup/embed forms open.
  // Klaviyo's official event is used so no page-wide DOM observer is needed.
  var _klaviyoCloseAriaInit = false;

  function initKlaviyoInlineFallback(root) {
    root = root || document;
    root.querySelectorAll('[data-gh-email-signup]').forEach(function (signup) {
      if (signup.dataset.ghEmailSignupInit) return;
      signup.dataset.ghEmailSignupInit = '1';

      var fallback = signup.querySelector('[data-gh-email-fallback]');
      var klaviyo = signup.querySelector('.gh-email-cap__klaviyo');
      if (!fallback || !klaviyo) return;

      // Preserve Shopify's returned success/error state instead of replacing it.
      var fallbackInput = fallback.querySelector('input[type="email"]');
      var visitorEngaged = Boolean(
        fallback.querySelector('.gh-email-cap__success, .gh-email-cap__error')
        || (fallbackInput && fallbackInput.value.trim())
      );

      function keepFallback() {
        visitorEngaged = true;
      }

      fallback.addEventListener('focusin', keepFallback, { once: true });
      fallback.addEventListener('input', keepFallback, { once: true });
      fallback.addEventListener('submit', keepFallback, { once: true });

      var observer = null;

      function revealKlaviyoWhenReady() {
        if (visitorEngaged || signup.classList.contains('gh-email-cap__signup--klaviyo-ready')) return;

        var input = klaviyo.querySelector('input[type="email"]');
        var button = klaviyo.querySelector('.klaviyo-form-button, button');
        if (!input || !button) return;

        fallback.hidden = true;
        klaviyo.setAttribute('aria-hidden', 'false');
        signup.classList.add('gh-email-cap__signup--klaviyo-ready');
        if (observer) observer.disconnect();
      }

      revealKlaviyoWhenReady();
      if (!signup.classList.contains('gh-email-cap__signup--klaviyo-ready') && !visitorEngaged) {
        observer = new MutationObserver(revealKlaviyoWhenReady);
        observer.observe(klaviyo, {
          childList: true,
          subtree: true
        });
      }
    });
  }

  function initKlaviyoCloseAria() {
    if (_klaviyoCloseAriaInit) return;
    _klaviyoCloseAriaInit = true;

    var locale = (window.Shopify && window.Shopify.locale)
      || document.documentElement.lang
      || 'en';
    locale = locale.toLowerCase().split('-')[0];

    var labels = {
      fr: {
        close: 'Fermer la fenêtre',
        attribution: 'Créé avec Klaviyo — s’ouvre dans un nouvel onglet'
      },
      de: {
        close: 'Dialog schließen',
        attribution: 'Erstellt mit Klaviyo — wird in einem neuen Tab geöffnet'
      },
      es: {
        close: 'Cerrar diálogo',
        attribution: 'Creado con Klaviyo — se abre en una pestaña nueva'
      }
    };
    var copy = labels[locale];
    if (!copy) return;

    var attributionSource = 'Created with Klaviyo - opens in a new tab';
    var retryDelays = [50, 100, 200, 400, 800, 1200];
    var retryTimer = null;
    var retryGeneration = 0;

    function applyKlaviyoCloseAria() {
      document.querySelectorAll('.klaviyo-close-form').forEach(function (button) {
        if (button.getAttribute('aria-label') !== copy.close) {
          button.setAttribute('aria-label', copy.close);
        }
        button.querySelectorAll('svg title').forEach(function (title) {
          if (title.textContent !== copy.close) title.textContent = copy.close;
        });
      });
    }

    function applyKlaviyoAttributionAria() {
      document.querySelectorAll('a[aria-label*="Klaviyo"]').forEach(function (link) {
        var current = link.getAttribute('aria-label');
        if (current !== attributionSource && current !== copy.attribution) return;
        if (current !== copy.attribution) link.setAttribute('aria-label', copy.attribution);
      });
    }

    function applyKlaviyoInlineLocale() {
      var container = document.querySelector('.gh-email-cap__klaviyo');
      if (!container) return;

      var form = container.querySelector('.klaviyo-form-SRD28y');
      if (!form) return;

      var placeholder = container.dataset.klaviyoEmailPlaceholder;
      var submitLabel = container.dataset.klaviyoSubmitLabel;
      var input = form.querySelector('input[type="email"]');
      var submit = form.querySelector('.klaviyo-form-button');

      if (input && placeholder) {
        if (input.placeholder !== placeholder) input.placeholder = placeholder;
        if (input.getAttribute('aria-label') !== placeholder) input.setAttribute('aria-label', placeholder);
      }
      if (submit && submitLabel) {
        var textNode = Array.prototype.find.call(submit.childNodes, function (node) {
          return node.nodeType === Node.TEXT_NODE && node.nodeValue.trim();
        });
        if (textNode && textNode.nodeValue.trim() !== submitLabel) {
          textNode.nodeValue = textNode.nodeValue.replace(textNode.nodeValue.trim(), submitLabel);
        }
      }
    }

    var inlineContainer = document.querySelector('.gh-email-cap__klaviyo');
    if (inlineContainer && !inlineContainer.dataset.ghKlaviyoLocaleObserver) {
      inlineContainer.dataset.ghKlaviyoLocaleObserver = '1';
      new MutationObserver(function () {
        applyKlaviyoInlineLocale();
      }).observe(inlineContainer, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['placeholder', 'aria-label']
      });
    }

    function applyKlaviyoLocaleUi() {
      applyKlaviyoCloseAria();
      applyKlaviyoAttributionAria();
      applyKlaviyoInlineLocale();
    }

    function scheduleKlaviyoLocaleUi() {
      retryGeneration += 1;
      var generation = retryGeneration;
      var retryIndex = 0;
      if (retryTimer) {
        window.clearTimeout(retryTimer);
        retryTimer = null;
      }

      function applyAndScheduleNext() {
        if (generation !== retryGeneration) return;
        applyKlaviyoLocaleUi();
        if (retryIndex >= retryDelays.length) {
          retryTimer = null;
          return;
        }
        retryTimer = window.setTimeout(applyAndScheduleNext, retryDelays[retryIndex]);
        retryIndex += 1;
      }

      requestAnimationFrame(applyAndScheduleNext);
    }

    window.addEventListener('klaviyoForms', function (event) {
      var eventType = event.detail && event.detail.type;
      if (eventType !== 'open' && eventType !== 'embedOpen') return;

      scheduleKlaviyoLocaleUi();
    });

    applyKlaviyoLocaleUi();
  }

  function initLifeHeroControls(root) {
    (root || document).querySelectorAll('[data-life-hero-controls]').forEach(function (controls) {
      if (controls.dataset.lifeHeroControlsInit === 'true') return;

      var media = controls.closest('.gh-life-intro-video');
      var video = media && media.querySelector('[data-life-hero-video]');
      if (!video) return;

      var playButton = controls.querySelector('[data-life-hero-play]');
      var soundButton = controls.querySelector('[data-life-hero-sound]');
      var playLabel = playButton && playButton.querySelector('[data-life-hero-play-label]');
      var soundLabel = soundButton && soundButton.querySelector('[data-life-hero-sound-label]');
      var playVideoLabel = (playButton && playButton.dataset.playLabel) || 'Play video';
      var pauseVideoLabel = (playButton && playButton.dataset.pauseLabel) || 'Pause video';
      var soundOnLabel = (soundButton && soundButton.dataset.soundOnLabel) || 'Sound on';
      var soundOffLabel = (soundButton && soundButton.dataset.soundOffLabel) || 'Sound off';

      function syncPlaybackState() {
        if (!playButton) return;
        var isPlaying = !video.paused && !video.ended;
        var controlLabel = isPlaying ? pauseVideoLabel : playVideoLabel;
        playButton.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
        playButton.setAttribute('aria-label', controlLabel);
        if (playLabel) playLabel.textContent = controlLabel;
      }

      function syncSoundState() {
        if (!soundButton) return;
        var soundIsOn = !video.muted && video.volume > 0;
        var controlLabel = soundIsOn ? soundOffLabel : soundOnLabel;
        soundButton.setAttribute('aria-pressed', soundIsOn ? 'true' : 'false');
        soundButton.setAttribute('aria-label', controlLabel);
        if (soundLabel) soundLabel.textContent = controlLabel;
      }

      controls.dataset.lifeHeroControlsInit = 'true';
      if (playButton) {
        playButton.addEventListener('click', function () {
          if (video.paused || video.ended) {
            var playPromise = video.play();
            if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(syncPlaybackState);
          } else {
            video.pause();
          }
          syncPlaybackState();
        });
      }
      if (soundButton) {
        soundButton.addEventListener('click', function () {
          var enableSound = video.muted || video.volume === 0;
          if (enableSound && video.volume === 0) video.volume = 1;
          video.muted = !enableSound;

          var playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(syncPlaybackState);
          syncSoundState();
        });
      }
      video.addEventListener('play', syncPlaybackState);
      video.addEventListener('pause', syncPlaybackState);
      video.addEventListener('ended', syncPlaybackState);
      video.addEventListener('volumechange', syncSoundState);
      syncPlaybackState();
      syncSoundState();
    });
  }

  function initCollabsLocalization(root) {
    var locale = ((document.documentElement.lang || 'en').toLowerCase().split('-')[0]);
    if (locale === 'en') return;

    root = root || document;
    var page = root.matches && root.matches('.collabs-page') ? root : root.querySelector('.collabs-page');
    if (!page || page.dataset.ghCollabsLocalized === locale) return;

    var intro = 'We offer free products, competitive affiliate commissions, and paid collaboration opportunities for selected creators. You’ll have the freedom to create content in your own style while sharing products you genuinely enjoy. We’re also open to building long-term partnerships with creators who are a great fit for our brand.';
    var translations = {
      de: {
        "Join the Goehner's community": 'Werden Sie Teil der Goehner’s Community',
        intro: 'Wir bieten ausgewählten Creators kostenlose Produkte, attraktive Affiliate-Provisionen und bezahlte Kooperationsmöglichkeiten. Sie können Inhalte in Ihrem eigenen Stil erstellen und Produkte vorstellen, die Sie wirklich gern nutzen. Mit Creators, die gut zu unserer Marke passen, bauen wir gerne langfristige Partnerschaften auf.',
        'Apply now': 'Jetzt bewerben',
        'Already a member?': 'Bereits Mitglied?',
        'Log in': 'Anmelden',
        'Partnership opportunities': 'Kooperationsmöglichkeiten',
        'Affiliate marketing': 'Affiliate-Marketing',
        'Gifting': 'Produktgeschenke',
        'Campaigns': 'Kampagnen',
        'Content creation': 'Content-Erstellung',
        'Usage rights': 'Nutzungsrechte',
        'Additional opportunities': 'Weitere Möglichkeiten',
        'Discount codes': 'Rabattcodes'
      },
      fr: {
        "Join the Goehner's community": 'Rejoignez la communauté Goehner’s',
        intro: 'Nous proposons aux créateurs sélectionnés des produits offerts, des commissions d’affiliation attractives et des collaborations rémunérées. Vous restez libre de créer dans votre propre style tout en partageant des produits que vous appréciez vraiment. Nous souhaitons aussi construire des partenariats durables avec les créateurs qui correspondent bien à notre marque.',
        'Apply now': 'Postuler maintenant',
        'Already a member?': 'Déjà membre ?',
        'Log in': 'Se connecter',
        'Partnership opportunities': 'Opportunités de partenariat',
        'Affiliate marketing': 'Marketing d’affiliation',
        'Gifting': 'Produits offerts',
        'Campaigns': 'Campagnes',
        'Content creation': 'Création de contenu',
        'Usage rights': 'Droits d’utilisation',
        'Additional opportunities': 'Autres opportunités',
        'Discount codes': 'Codes de réduction'
      },
      es: {
        "Join the Goehner's community": 'Únase a la comunidad de Goehner’s',
        intro: 'Ofrecemos a creadores seleccionados productos gratuitos, comisiones de afiliación competitivas y oportunidades de colaboración remunerada. Tendrá libertad para crear contenido con su propio estilo mientras comparte productos que realmente disfruta. También queremos construir colaboraciones a largo plazo con creadores que encajen bien con nuestra marca.',
        'Apply now': 'Solicitar ahora',
        'Already a member?': '¿Ya es miembro?',
        'Log in': 'Iniciar sesión',
        'Partnership opportunities': 'Oportunidades de colaboración',
        'Affiliate marketing': 'Marketing de afiliación',
        'Gifting': 'Productos de regalo',
        'Campaigns': 'Campañas',
        'Content creation': 'Creación de contenido',
        'Usage rights': 'Derechos de uso',
        'Additional opportunities': 'Otras oportunidades',
        'Discount codes': 'Códigos de descuento'
      }
    };
    var localeMap = translations[locale];
    if (!localeMap) return;

    function normalizeText(value) {
      return String(value || '').replace(/\s+/g, ' ').trim();
    }

    page.querySelectorAll('h1, h2, p, a, li').forEach(function (element) {
      if (element.children.length > 0) return;
      var englishText = normalizeText(element.textContent);
      var translation = englishText === intro ? localeMap.intro : localeMap[englishText];
      if (translation) element.textContent = translation;
    });
    page.dataset.ghCollabsLocalized = locale;
  }

  // Creator post library: lightweight cards + one official platform player at a time.
  function initUgcLibrary(root) {
    root = root || document;
    root.querySelectorAll('[data-ugc-library]:not([data-ugc-library-init])').forEach(function (library) {
      library.setAttribute('data-ugc-library-init', '');

      var translations = {};
      var translationNode = library.querySelector('[data-ugc-i18n]');
      try { translations = JSON.parse(translationNode ? translationNode.textContent : '{}'); } catch (error) { /* Keep server-rendered labels if translation data is unavailable. */ }
      function ugcText(key, values) {
        var text = translations[key] || '';
        Object.keys(values || {}).forEach(function (name) {
          text = text.split('__' + name + '__').join(String(values[name]));
        });
        return text;
      }

      var ugcLocale = library.getAttribute('data-ugc-locale') || document.documentElement.lang || 'en';
      library.querySelectorAll('[data-ugc-views]').forEach(function (badge) {
        var count = Number(badge.getAttribute('data-ugc-views'));
        var label = badge.querySelector('[data-ugc-views-label]');
        if (!Number.isFinite(count) || count <= 0 || !label || !translations.views) return;
        try {
          var compact = new Intl.NumberFormat(ugcLocale, { notation: 'compact', maximumFractionDigits: 1 }).format(count);
          label.textContent = ugcText('views', { number: compact });
          badge.title = ugcText('views_title', { number: new Intl.NumberFormat(ugcLocale).format(count), platform: badge.getAttribute('data-platform-label') || '' });
          var checkedAt = badge.getAttribute('data-checked-at');
          if (checkedAt) badge.title += ' · ' + ugcText('checked', { date: checkedAt });
        } catch (error) { /* The localized server-rendered snapshot is the fallback. */ }
      });

      var items = Array.prototype.slice.call(library.querySelectorAll('[data-ugc-item]'));
      var loadMore = library.querySelector('[data-ugc-more]');
      var search = library.querySelector('[data-ugc-search]');
      var searchStatus = library.querySelector('[data-ugc-search-status]');
      var empty = library.querySelector('[data-ugc-empty]');
      var results = library.querySelector('[data-ugc-results]');
      var modal = library.querySelector('[data-ugc-modal]');
      var dialog = modal && modal.querySelector('.gh-ugc-library__dialog');
      var embed = modal && modal.querySelector('[data-ugc-embed]');
      var modalTitle = modal && modal.querySelector('[data-ugc-modal-title]');
      var modalKicker = modal && modal.querySelector('[data-ugc-modal-kicker]');
      var modalHelp = modal && modal.querySelector('[data-ugc-modal-help]');
      var original = modal && modal.querySelector('[data-ugc-original]');
      var closeButton = modal && modal.querySelector('.gh-ugc-library__close');
      var pageSize = parseInt(library.getAttribute('data-page-size'), 10) || 12;
      var visibleLimit = pageSize;
      var lastTrigger = null;
      var viewport = window.visualViewport;
      var layoutFrame = 0;

      function fitModalViewport() {
        layoutFrame = 0;
        if (!modal || modal.hidden || !dialog) return;
        var width = viewport ? viewport.width : window.innerWidth;
        var height = viewport ? viewport.height : window.innerHeight;
        modal.style.setProperty('--ugc-viewport-width', width + 'px');
        modal.style.setProperty('--ugc-viewport-height', height + 'px');
        modal.style.setProperty('--ugc-viewport-left', (viewport ? viewport.offsetLeft : 0) + 'px');
        modal.style.setProperty('--ugc-viewport-top', (viewport ? viewport.offsetTop : 0) + 'px');
        var head = dialog.querySelector('.gh-ugc-library__dialog-head');
        var foot = dialog.querySelector('.gh-ugc-library__dialog-foot');
        var portrait = dialog.classList.contains('is-portrait-player');
        var horizontal = dialog.classList.contains('is-horizontal');
        var ratio = portrait ? 9 / 16 : horizontal ? 16 / 9 : 0;
        var fittedWidth = Math.min(Math.max(0, width - 32), horizontal ? 960 : portrait ? 405 : 460);
        dialog.style.width = fittedWidth + 'px';
        for (var pass = 0; pass < 3; pass += 1) {
          var chromeHeight = (head ? head.getBoundingClientRect().height : 0) + (foot ? foot.getBoundingClientRect().height : 0);
          var mediaHeight = Math.max(1, height - 34 - chromeHeight);
          dialog.style.setProperty('--ugc-media-height', mediaHeight + 'px');
          if (!ratio) break;
          fittedWidth = Math.min(fittedWidth, mediaHeight * ratio);
          dialog.style.width = fittedWidth + 'px';
        }
      }

      function scheduleModalLayout() {
        if (!layoutFrame) layoutFrame = window.requestAnimationFrame(fitModalViewport);
      }

      function watchModalViewport(active) {
        var method = active ? 'addEventListener' : 'removeEventListener';
        window[method]('resize', scheduleModalLayout);
        if (viewport) {
          viewport[method]('resize', scheduleModalLayout);
          viewport[method]('scroll', scheduleModalLayout);
        }
        if (!active && layoutFrame) {
          window.cancelAnimationFrame(layoutFrame);
          layoutFrame = 0;
        }
      }

      var shopLink = library.querySelector('[data-ugc-shop]');
      var shopLayoutFrame = 0;

      function fitShopLinkViewport() {
        shopLayoutFrame = 0;
        if (!shopLink || document.body.classList.contains('gh-ugc-library-open')) return;
        var width = viewport ? viewport.width : window.innerWidth;
        var height = viewport ? viewport.height : window.innerHeight;
        var left = viewport ? viewport.offsetLeft : 0;
        var top = viewport ? viewport.offsetTop : 0;
        var linkWidth = shopLink.offsetWidth;
        var halfHeight = shopLink.offsetHeight / 2;
        shopLink.style.right = 'auto';
        shopLink.style.left = Math.max(left + 8, left + width - linkWidth - 8) + 'px';
        shopLink.style.top = Math.max(top + halfHeight + 8, Math.min(top + height * 0.4, top + height - halfHeight - 8)) + 'px';
      }

      function scheduleShopLinkLayout() {
        if (!shopLayoutFrame) shopLayoutFrame = window.requestAnimationFrame(fitShopLinkViewport);
      }

      if (shopLink) {
        var productHandle = new URLSearchParams(window.location.search).get('product') || '';
        if (/^[a-z0-9][a-z0-9-]{0,120}$/.test(productHandle)) {
          var productBase = shopLink.getAttribute('data-product-base') || '/products/';
          if (/^\/(?:[a-z]{2,3}(?:-[a-zA-Z]{2,4})?\/)?products\/$/.test(productBase)) {
            shopLink.href = productBase + encodeURIComponent(productHandle);
          }
        }
        fitShopLinkViewport();
        window.addEventListener('resize', scheduleShopLinkLayout);
        if (viewport) {
          viewport.addEventListener('resize', scheduleShopLinkLayout);
          viewport.addEventListener('scroll', scheduleShopLinkLayout);
        }
      }

      function clearEmbed() {
        if (!embed) return;
        embed.querySelectorAll('video').forEach(function (video) {
          video.pause();
          video.removeAttribute('src');
          video.load();
        });
        while (embed.firstChild) embed.removeChild(embed.firstChild);
      }

      function validateHostedVideoUrl(value) {
        try {
          var parsed = new URL(value, window.location.origin);
          var shopCdn = parsed.hostname === window.location.hostname && parsed.pathname.indexOf('/cdn/shop/videos/') === 0;
          var sharedCdn = parsed.hostname === 'cdn.shopify.com' && parsed.pathname.indexOf('/videos/') === 0;
          return parsed.protocol === 'https:' && !parsed.username && !parsed.password
            && (shopCdn || sharedCdn) && /\.mp4$/i.test(parsed.pathname) ? parsed.href : '';
        } catch (error) {
          return '';
        }
      }

      function validateOriginalUrl(value, platform) {
        try {
          var parsed = new URL(value, window.location.origin);
          var host = parsed.hostname.toLowerCase().replace(/^www\./, '');
          var allowed = {
            tiktok: ['tiktok.com'],
            instagram: ['instagram.com'],
            youtube: ['youtube.com', 'youtu.be']
          };
          return parsed.protocol === 'https:'
            && !parsed.username
            && !parsed.password
            && allowed[platform]
            && allowed[platform].indexOf(host) !== -1
            ? parsed.href
            : '';
        } catch (error) {
          return '';
        }
      }

      function buildEmbedSrc(trigger) {
        var platform = trigger.getAttribute('data-platform');
        var postId = (trigger.getAttribute('data-post-id') || '').trim();
        var postPath = (trigger.getAttribute('data-post-path') || '').trim().replace(/^\/+|\/+$/g, '');

        if (platform === 'tiktok' && /^\d{10,22}$/.test(postId)) {
          return 'https://www.tiktok.com/player/v1/' + encodeURIComponent(postId)
            + '?loop=0&description=1&music_info=1&rel=0&native_context_menu=0&closed_caption=1';
        }
        if (platform === 'youtube' && /^[A-Za-z0-9_-]{11}$/.test(postId)) {
          return 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(postId) + '?rel=0&hl=en';
        }
        if (platform === 'instagram' && /^(p|reel|tv)\/[A-Za-z0-9_-]+$/.test(postPath)) {
          return 'https://www.instagram.com/' + postPath + '/embed/';
        }
        return '';
      }

      function closeModal() {
        if (!modal || modal.hidden) return;
        watchModalViewport(false);
        if (modal.open && typeof modal.close === 'function') modal.close();
        modal.hidden = true;
        clearEmbed();
        document.body.classList.remove('gh-ugc-library-open');
        document.documentElement.classList.remove('gh-ugc-library-open');
        if (shopLink) scheduleShopLinkLayout();
        if (dialog) {
          dialog.classList.remove('is-horizontal', 'is-portrait-player');
          dialog.style.removeProperty('width');
          dialog.style.removeProperty('--ugc-media-height');
        }
        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(lastTrigger && document.contains(lastTrigger) ? lastTrigger : null, dialog);
        } else if (lastTrigger && document.contains(lastTrigger)) {
          lastTrigger.focus();
        }
      }

      function openModal(trigger) {
        if (!modal || !embed || !dialog) return;
        var platform = trigger.getAttribute('data-platform') || '';
        var hostedSrc = platform === 'shopify' ? validateHostedVideoUrl(trigger.getAttribute('data-video-src') || '') : '';
        var originalUrl = validateOriginalUrl(trigger.getAttribute('data-post-url') || '', platform);
        if (hostedSrc) originalUrl = hostedSrc;
        var embedSrc = buildEmbedSrc(trigger);
        var creator = trigger.getAttribute('data-creator') || ugcText('creator_post');
        var aspect = trigger.getAttribute('data-aspect') || 'vertical';

        lastTrigger = trigger;
        clearEmbed();
        dialog.classList.toggle('is-horizontal', aspect === 'horizontal');
        dialog.classList.toggle('is-portrait-player', aspect !== 'horizontal' && (platform === 'tiktok' || platform === 'youtube' || platform === 'shopify'));
        if (modalTitle) modalTitle.textContent = creator;
        if (modalKicker) modalKicker.textContent = ugcText(platform === 'shopify' ? 'community_video' : 'original_post');
        if (modalHelp) modalHelp.textContent = ugcText(platform === 'shopify' ? 'hosted_help' : 'platform_help');
        if (original) {
          original.textContent = ugcText(platform === 'shopify' ? 'open_video' : 'view_original');
          original.hidden = !originalUrl;
          if (originalUrl) original.href = originalUrl;
          else original.removeAttribute('href');
        }

        var hostedPlayer = null;
        if (hostedSrc) {
          hostedPlayer = document.createElement('video');
          hostedPlayer.controls = true;
          hostedPlayer.playsInline = true;
          hostedPlayer.preload = 'metadata';
          hostedPlayer.setAttribute('aria-label', creator);
          hostedPlayer.poster = trigger.getAttribute('data-video-poster') || '';
          hostedPlayer.src = hostedSrc;
          embed.appendChild(hostedPlayer);
        } else if (embedSrc) {
          var iframe = document.createElement('iframe');
          iframe.src = embedSrc;
          iframe.title = ugcText('iframe_title', { platform: trigger.getAttribute('data-platform-label') || platform, creator: creator });
          iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
          iframe.allowFullscreen = true;
          iframe.referrerPolicy = 'strict-origin-when-cross-origin';
          iframe.setAttribute('tabindex', '0');
          embed.appendChild(iframe);
        } else {
          var message = document.createElement('p');
          message.textContent = ugcText('unavailable');
          embed.appendChild(message);
        }

        modal.hidden = false;
        if (typeof modal.showModal === 'function' && !modal.open) modal.showModal();
        document.body.classList.add('gh-ugc-library-open');
        document.documentElement.classList.add('gh-ugc-library-open');
        fitModalViewport();
        watchModalViewport(true);
        if (hostedPlayer) {
          var playRequest = hostedPlayer.play();
          if (playRequest && typeof playRequest.catch === 'function') playRequest.catch(function () { /* Native controls remain available if playback is blocked. */ });
        }
        if (typeof trapFocus === 'function') trapFocus(dialog, closeButton || dialog);
        else if (closeButton) closeButton.focus();
      }

      function handleDocumentKeydown(event) {
        if (event.key === 'Escape' && modal && !modal.hidden) closeModal();
      }

      function handleModalCancel(event) {
        event.preventDefault();
        closeModal();
      }

      function handleSectionUnload(event) {
        var sectionRoot = event.target;
        if (sectionRoot !== library && !(sectionRoot && sectionRoot.contains(library))) return;
        closeModal();
        document.removeEventListener('keydown', handleDocumentKeydown);
        document.removeEventListener('shopify:section:unload', handleSectionUnload);
        if (modal) modal.removeEventListener('cancel', handleModalCancel);
        window.removeEventListener('resize', scheduleShopLinkLayout);
        if (viewport) {
          viewport.removeEventListener('resize', scheduleShopLinkLayout);
          viewport.removeEventListener('scroll', scheduleShopLinkLayout);
        }
        if (shopLayoutFrame) window.cancelAnimationFrame(shopLayoutFrame);
        if (search) search.removeEventListener('input', handleSearchInput);
        library.removeAttribute('data-ugc-library-init');
      }

      function renderCards() {
        var query = search ? search.value.trim().toLowerCase() : '';
        var terms = query ? query.split(/\s+/) : [];
        var matches = items.filter(function (item) {
          var text = (item.getAttribute('data-ugc-search-text') || '').toLowerCase();
          return terms.every(function (term) { return text.indexOf(term) !== -1; });
        });

        items.forEach(function (item) { item.hidden = true; });
        matches.forEach(function (item, index) { item.hidden = index >= visibleLimit; });
        if (loadMore) loadMore.hidden = matches.length <= visibleLimit;
        if (empty) empty.hidden = matches.length > 0;
        if (results) {
          var shown = Math.min(matches.length, visibleLimit);
          results.textContent = ugcText(shown === 0 ? 'results_none' : shown === 1 ? 'results_one' : 'results_many', { number: shown });
        }
        if (searchStatus) {
          searchStatus.textContent = ugcText(query ? 'posts_found' : 'posts_available', { number: matches.length });
        }
      }

      function handleSearchInput() {
        visibleLimit = pageSize;
        renderCards();
      }

      if (search) search.addEventListener('input', handleSearchInput);
      if (loadMore) {
        loadMore.addEventListener('click', function () {
          visibleLimit += pageSize;
          renderCards();
        });
      }

      library.addEventListener('click', function (event) {
        var trigger = event.target.closest('[data-ugc-open]');
        if (trigger && library.contains(trigger)) openModal(trigger);
      });

      if (modal) {
        modal.addEventListener('cancel', handleModalCancel);
        modal.querySelectorAll('[data-ugc-close]').forEach(function (control) {
          control.addEventListener('click', closeModal);
        });
      }

      document.addEventListener('keydown', handleDocumentKeydown);
      document.addEventListener('shopify:section:unload', handleSectionUnload);

      renderCards();
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Boot: init on DOM ready + re-init on Shopify section reload
  // ─────────────────────────────────────────────────────────────
  function boot() {
    initLazyVideos(document);
    initSteamaxImages(document);
    initUgcScroll(document);
    initBlogScroll(document);
    initCarouselCounters(document);
    initStickyNav(document);
    initPdpGallery(document);
    initPdpDemoVideo(document);
    initPdpFullRun(document);
    initPdpVideoChapters(document);
    initPdpColorQueryLinks(document);
    initPdpVariantSelect(document);
    initPdpQtyStepper(document);
    initPdpPromoTimer(document);
    initPdpAddToCart(document);
    initPdpDirectCheckout(document);
    initPdpUseTabs(document);
    initPdpSceneFold(document);
    initPdpAboutTabs(document);
    initPdpFeatureCarousel();
    initPdpAnchorNavigation();
    initRoadmapTabs(document);
    initPdpLightbox(document);
    initPdpStickyCta(document);
    initPdpModuleFolds(document);
    initJudgeMeMediaFallback(document);
    initJudgeMePaginationSync(document);
    initJudgeMePresentation(document);
    initJudgeMeLocalizedMetadata(document);
    initJudgeMeReviewerIdentity(document);
    initJudgeMeAmazonSourceLabels(document);
    initJudgeMeReviewFolds(document);
    initKlaviyoInlineFallback(document);
    initKlaviyoCloseAria();
    initLangPicker(document);
    initHeaderV2(document);
    initNewsletterPopup();
    initCartPage(document);
    initPageOrderStatus();
    initPageThankYou();
    initPageEmailPreferences();
    initPageSupport();
    initPage500();
    initBackToTop();
    initMailtoLinks();
    initAnnounceBar(document);
    initCollectionSort(document);
    initCollectionATC(document);
    initCommerceClickTracking();
    initArticleToc();
    initLifeHeroControls(document);
    initCollabsLocalization(document);
    initUgcLibrary(document);
    // Gift card JS is inline in templates/gift_card.liquid (standalone {% layout none %} template)
    // Accessibility page has no JS
    // Wholesale page: no JS needed (form uses Shopify native {% form 'contact' %})
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify Theme Editor: re-scan when a section's HTML is replaced
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target || document;
    initLazyVideos(root);
    initSteamaxImages(root);
    initUgcScroll(root);
    initBlogScroll(root);
    initCarouselCounters(root);
    initStickyNav(root);
    initPdpGallery(root);
    initPdpDemoVideo(root);
    initPdpFullRun(root);
    initPdpVideoChapters(root);
    initPdpColorQueryLinks(root);
    initPdpVariantSelect(root);
    initPdpQtyStepper(root);
    initPdpPromoTimer(root);
    initPdpAddToCart(root);
    initPdpDirectCheckout(root);
    initPdpUseTabs(root);
    initPdpSceneFold(root);
    initPdpAboutTabs(root);
    initPdpFeatureCarousel();
    initPdpAnchorNavigation();
    initRoadmapTabs(root);
    initPdpLightbox(root);
    initPdpStickyCta(root);
    initPdpModuleFolds(root);
    initJudgeMeMediaFallback(root);
    initJudgeMePaginationSync(root);
    initJudgeMePresentation(root);
    initJudgeMeLocalizedMetadata(root);
    initJudgeMeReviewerIdentity(root);
    initJudgeMeAmazonSourceLabels(root);
    initJudgeMeReviewFolds(root);
    initKlaviyoInlineFallback(root);
    initLangPicker(root);
    initHeaderV2(root);
    initMailtoLinks();
    initAnnounceBar(root);
    initCollectionSort(root);
    initLifeHeroControls(root);
    initCollabsLocalization(root);
    initUgcLibrary(root);
  });

  // ─────────────────────────────────────────────────────────────
  // 9. Newsletter Popup (gh-ep)
  //    V13 §16 logic: 8s delay, localStorage gh_ep_dismissed,
  //    close (session only) vs decline (permanent dismiss).
  //    data-delay attribute on #email-popup overrides default ms.
  //    data-success="true" means Shopify form.posted_successfully:
  //      show popup immediately → auto-close after 1.8s + dismiss.
  //    window.ghSubmitEmailPopup removed; Shopify form handles POST.
  // ─────────────────────────────────────────────────────────────
  function initNewsletterPopup() {
    if (document.querySelector('[data-ugc-library]:not([data-ugc-home-preview])')) return;
    var epOverlay = document.getElementById('email-popup');
    if (!epOverlay) return;
    // Guard against double-init
    if (epOverlay.dataset.epInit) return;
    epOverlay.dataset.epInit = '1';

    var epClose   = document.getElementById('ep-close');
    var epDecline = document.getElementById('ep-decline');
    var delayMs   = parseInt(epOverlay.getAttribute('data-delay'), 10);
    if (isNaN(delayMs) || delayMs < 0) delayMs = 8000;

    // Per-page-load flag: once opened, don't reopen on this same page load.
    var epShownThisPage = false;

    // Cross-page suppression uses localStorage, NOT sessionStorage: mobile
    // browsers do not reliably carry sessionStorage across page navigations.
    //   gh_ep_dismissed = '1'       → declined, never show again
    //   gh_ep_closed    = <millis>  → closed/shown, 24h cooldown before re-show
    var EP_COOLDOWN_MS = 24 * 60 * 60 * 1000;
    function epSuppressed() {
      try {
        if (localStorage.getItem('gh_ep_dismissed')) return true;
        var closed = parseInt(localStorage.getItem('gh_ep_closed'), 10);
        if (!isNaN(closed) && (Date.now() - closed) < EP_COOLDOWN_MS) return true;
      } catch (e) {}
      return false;
    }

    function openEp() {
      if (document.body.classList.contains('gh-ugc-library-open')) return;
      if (epShownThisPage) return;
      epShownThisPage = true;
      // Mark shown now, so navigating away without closing won't re-trigger it.
      try { localStorage.setItem('gh_ep_closed', String(Date.now())); } catch (e) {}
      epOverlay.classList.add('is-open');
      epOverlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
    function closeEp(dismissed) {
      epOverlay.classList.remove('is-open');
      epOverlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      try {
        if (dismissed) { localStorage.setItem('gh_ep_dismissed', '1'); }
        else { localStorage.setItem('gh_ep_closed', String(Date.now())); }
      } catch (e) {}
    }

    if (epClose)   epClose.addEventListener('click',   function () { closeEp(false); });
    if (epDecline) epDecline.addEventListener('click', function () { closeEp(true); });

    // If Shopify form returned successfully, show success state immediately
    if (epOverlay.getAttribute('data-success') === 'true') {
      openEp();
      setTimeout(function () { closeEp(true); }, 1800);
      return;
    }

    // Normal delayed trigger — skip if dismissed or closed within the cooldown
    if (!epSuppressed()) {
      setTimeout(function () {
        if (!epSuppressed()) openEp();
      }, delayMs);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // 10. Cart Page (gh-cart-page)
  //     Cart quantity controls and error retry.
  //     Number input and +/- buttons share Shopify's Ajax cart API.
  //     Double-init guard: dataset.cpInit.
  // ─────────────────────────────────────────────────────────────
  function initCartPage(root) {
    var cartPage = root.querySelector('.gh-cart-page');
    if (!cartPage) return;
    // Guard against double-init independently of optional cart UI.
    if (cartPage.dataset.cpInit) return;
    cartPage.dataset.cpInit = '1';

    // ── Qty stepper ─────────────────────────────────────────
    // Quantity inputs carry Shopify's stable line-item key, including on retry.
    var errorBox = root.querySelector('[data-cart-update-error]');
    var errorText = errorBox && errorBox.querySelector('[data-cart-update-error-text]');
    var retryButton = errorBox && errorBox.querySelector('[data-cart-update-retry]');
    var pendingUpdate = null;
    var updating = false;
    var quantityInputs = Array.prototype.slice.call(cartPage.querySelectorAll('.gh-cart-page-item__qty-val'));
    var quantityButtons = cartPage.querySelectorAll('.gh-cart-page-item__qty-btn');
    var checkoutButton = cartPage.querySelector('[name="checkout"]');
    var cartForm = cartPage.querySelector('#gh-cart-form');

    function hasQuantityEdits() {
      return quantityInputs.some(function (input) { return input.value !== input.dataset.confirmedQuantity; });
    }

    function syncCheckout() {
      if (checkoutButton) checkoutButton.disabled = updating || !!pendingUpdate || hasQuantityEdits();
    }

    function setUpdating(busy) {
      updating = busy;
      quantityInputs.forEach(function (input) { input.disabled = busy; });
      quantityButtons.forEach(function (button) { button.disabled = busy; });
      if (retryButton) retryButton.disabled = busy;
      syncCheckout();
    }

    function showCartUpdateError() {
      if (!errorBox) return;
      if (errorText) errorText.textContent = errorBox.getAttribute('data-error-message') || '';
      errorBox.hidden = false;
    }

    function hideCartUpdateError() {
      if (errorBox) errorBox.hidden = true;
    }

    function cartChangeUrl() {
      var url = getThemeRoute('cart_change_url', 'cart/change');
      return /\.js(?:\?|$)/.test(url) ? url : url.replace(/\/$/, '') + '.js';
    }

    function updateCartLine(update) {
      if (!update || updating) return;
      pendingUpdate = update;
      hideCartUpdateError();
      setUpdating(true);
      update.input.setAttribute('aria-busy', 'true');

      fetch(cartChangeUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: update.key, quantity: update.quantity })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('');
          return response.json();
        })
        .then(function () { window.location.reload(); })
        .catch(function () {
          update.input.value = update.previousQuantity;
          update.input.removeAttribute('aria-busy');
          setUpdating(false);
          showCartUpdateError();
        });
    }

    if (retryButton) {
      retryButton.addEventListener('click', function () {
        if (!pendingUpdate || updating) return;
        var update = pendingUpdate;
        // A failed response may follow a successful removal. Reconcile before
        // retrying so an absent line is refreshed rather than mutated again.
        setUpdating(true);
        var url = getThemeRoute('cart_url', 'cart');
        if (!/\.js(?:\?|$)/.test(url)) url = url.replace(/\/$/, '') + '.js';
        fetch(url, { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
          .then(function (response) {
            if (!response.ok) throw new Error('');
            return response.json();
          })
          .then(function (cart) {
            if (!cart || !Array.isArray(cart.items)) throw new Error('');
            var item = cart.items.find(function (line) { return line.key === update.key; });
            if (!item || item.quantity === update.quantity) {
              window.location.reload();
              return;
            }
            setUpdating(false);
            updateCartLine(update);
          })
          .catch(function () {
            setUpdating(false);
            showCartUpdateError();
          });
      });
    }

    quantityInputs.forEach(function (input) {
      var group = input.closest('.gh-cart-page-item__qty');
      if (!group) return;
      var buttons = group.querySelectorAll('.gh-cart-page-item__qty-btn');
      var key = input.getAttribute('data-cart-line-key');
      input.dataset.confirmedQuantity = input.value;

      function commitQuantity() {
        if (updating) return;
        var raw = input.value.trim();
        var next = Number(raw);
        var min = input.min === '' ? 0 : Number(input.min);
        var max = input.max === '' ? Infinity : Number(input.max);
        if (!/^\d+$/.test(raw) || !Number.isInteger(next) || next < min || next > max || !input.checkValidity()) {
          input.value = input.dataset.confirmedQuantity;
          syncCheckout();
          return;
        }
        if (next === Number(input.dataset.confirmedQuantity) && !pendingUpdate) {
          input.value = input.dataset.confirmedQuantity;
          syncCheckout();
          return;
        }
        if (!key) return;
        updateCartLine({ key: key, quantity: next, previousQuantity: input.dataset.confirmedQuantity, input: input });
      }

      input.addEventListener('input', syncCheckout);
      input.addEventListener('change', commitQuantity);
      input.addEventListener('blur', commitQuantity);
      input.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' && (pendingUpdate || hasQuantityEdits())) {
          event.preventDefault();
          commitQuantity();
        }
      });
      buttons.forEach(function (button) {
        button.addEventListener('click', function () {
          if (updating) return;
          var step = Number(button.getAttribute('data-qty-step'));
          var current = Number(input.dataset.confirmedQuantity);
          var min = input.min === '' ? 0 : Number(input.min);
          var max = input.max === '' ? Infinity : Number(input.max);
          input.value = Math.max(min, Math.min(max, current + step));
          commitQuantity();
        });
      });
    });

    if (cartForm) {
      cartForm.addEventListener('submit', function (event) {
        if (updating || pendingUpdate || hasQuantityEdits()) event.preventDefault();
      });
    }
    cartPage.querySelectorAll('.gh-cart-page-item__remove').forEach(function (link) {
      link.addEventListener('click', function (event) { if (updating) event.preventDefault(); });
    });

  }

  // ─────────────────────────────────────────────────────────────
  // Email Preferences Page — authenticated Shopify unsubscribe only
  // Used by: custom-page-email-preferences (templates/page.email-preferences.json)
  // Guest visitors receive the provider-issued unsubscribe-link instructions;
  // they never get an unidentified customer form.
  // Double-init guard: data-ep-init on the card element
  // ─────────────────────────────────────────────────────────────
  function initPageEmailPreferences() {
    var card = document.querySelector('.gh-emailpref__card');
    if (!card) return;
    if (card.dataset.epInit) return;
    card.dataset.epInit = '1';

    // Unsubscribe all — confirm before the authenticated Shopify form submit.
    var unsubForm = document.getElementById('emailpref-unsub-form');
    if (unsubForm) {
      unsubForm.addEventListener('submit', function (e) {
        var message = unsubForm.getAttribute('data-confirm-message') || '';
        var ok = !message || confirm(message);
        if (!ok) e.preventDefault();
      });
    }
  }

  // ─────────────────────────────────────────────────────────────
  // initPageSupport — Support page accordion + category collapse + smooth scroll
  // Used by: custom-page-support.liquid
  // ─────────────────────────────────────────────────────────────
  function initPageSupport() {
    var page = document.querySelector('[data-support-page]');
    if (!page) return;
    if (page.dataset.spInit) return;
    page.dataset.spInit = '1';

    // ── Category collapse: each [data-faq-cat] h2 toggles its adjacent .gh-support-faq__list ──
    function openCategory(catEl) {
      if (!catEl || !catEl.hasAttribute('data-faq-cat')) return;
      catEl.classList.add('is-open');
      catEl.setAttribute('aria-expanded', 'true');
    }
    function closeCategory(catEl) {
      if (!catEl) return;
      catEl.classList.remove('is-open');
      catEl.setAttribute('aria-expanded', 'false');
    }
    function toggleCategory(catEl) {
      if (catEl.classList.contains('is-open')) {
        closeCategory(catEl);
      } else {
        openCategory(catEl);
      }
    }

    page.querySelectorAll('[data-faq-cat]').forEach(function (cat) {
      cat.addEventListener('click', function () {
        toggleCategory(cat);
      });
      cat.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleCategory(cat);
        }
      });
    });

    // ── Per-item accordion inside each category list ──
    page.querySelectorAll('.gh-support-faq__q').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var item = btn.closest('.gh-support-faq__item');
        var answer = item.querySelector('.gh-support-faq__a');
        var isOpen = item.classList.contains('is-open');

        // Close all siblings in same list
        var list = item.closest('.gh-support-faq__list');
        if (list) {
          list.querySelectorAll('.gh-support-faq__item.is-open').forEach(function (openItem) {
            if (openItem !== item) {
              openItem.classList.remove('is-open');
              var openBtn = openItem.querySelector('.gh-support-faq__q');
              var openAns = openItem.querySelector('.gh-support-faq__a');
              if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
              if (openAns) openAns.hidden = true;
            }
          });
        }

        // Toggle current
        if (isOpen) {
          item.classList.remove('is-open');
          btn.setAttribute('aria-expanded', 'false');
          if (answer) answer.hidden = true;
        } else {
          item.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
          if (answer) answer.hidden = false;
        }
      });
    });

    // ── Smooth scroll for anchor links + auto-open category on anchor nav ──
    page.querySelectorAll('a[href^="#"]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        var targetId = link.getAttribute('href').slice(1);
        var target = document.getElementById(targetId);
        if (!target) return;
        e.preventDefault();

        // Auto-open the category if the target is a collapsible [data-faq-cat] h2
        if (target.hasAttribute('data-faq-cat')) {
          openCategory(target);
        }

        var navH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--nav-h') || '0', 10);
        var top = target.getBoundingClientRect().top + window.scrollY - navH - 16;
        window.scrollTo({ top: top, behavior: 'smooth' });
      });
    });
  }

  // ─────────────────────────────────────────────────────────────
  // initPage500 — 500 error page countdown timer + auto reload
  // Used by: custom-page-500.liquid
  // ─────────────────────────────────────────────────────────────
  function initPage500() {
    var retryEl = document.querySelector('[data-500-countdown]');
    if (!retryEl) return;
    if (retryEl.dataset.e5Init) return;
    retryEl.dataset.e5Init = '1';

    var totalSeconds = parseInt(retryEl.getAttribute('data-500-countdown'), 10) || 10;
    var shouldReload = retryEl.getAttribute('data-500-reload') === 'true';
    var countdownEl = document.getElementById('gh-500-countdown');
    var retryNowBtn = document.getElementById('gh-500-retry-now');
    var remaining = totalSeconds;

    function tick() {
      remaining -= 1;
      if (countdownEl) countdownEl.textContent = remaining;
      if (remaining <= 0) {
        clearInterval(timer);
        if (shouldReload) window.location.reload();
      }
    }

    var timer = setInterval(tick, 1000);

    if (retryNowBtn) {
      retryNowBtn.addEventListener('click', function () {
        clearInterval(timer);
        window.location.reload();
      });
    }
  }

  // ─────────────────────────────────────────────────────────────
  // initBackToTop — floating back-to-top button (left side, bottom)
  // Creates a singleton <button class="gh-back-to-top"> appended to body.
  // Appears when scrollY > 1.5× viewport height (long pages only).
  // Smooth scroll respects prefers-reduced-motion.
  // Idempotent via module-level flag.
  // ─────────────────────────────────────────────────────────────
  var _backToTopInit = false;

  function initBackToTop() {
    if (_backToTopInit) return;
    _backToTopInit = true;

    var backToTopLabel = window.accessibilityStrings && window.accessibilityStrings.backToTop;
    if (!backToTopLabel) return;

    var btn = document.createElement('button');
    btn.className = 'gh-back-to-top';
    btn.setAttribute('aria-label', backToTopLabel);

    // Up-arrow SVG — line-art, stroke=currentColor
    btn.innerHTML = '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">'
      + '<polyline points="3,12 9,6 15,12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>'
      + '</svg>';

    document.body.appendChild(btn);

    function syncStickyOffset() {
      if (window.matchMedia('(max-width: 1023px)').matches) {
        var mobileBottom = 88;
        var mobileStickyCta = document.querySelector('[data-pdp-sticky].is-visible');
        if (mobileStickyCta && !mobileStickyCta.hasAttribute('data-pdp-sticky-top') && window.getComputedStyle(mobileStickyCta).display !== 'none') {
          mobileBottom = Math.max(mobileBottom, Math.ceil(mobileStickyCta.getBoundingClientRect().height + 16));
        }
        btn.style.bottom = 'calc(' + mobileBottom + 'px + env(safe-area-inset-bottom, 0px))';
        return;
      }
      btn.style.removeProperty('bottom');
    }

    // Scroll listener with rAF throttle
    var _rafPending = false;

    function isSceneWallVisible() {
      var sceneWall = document.querySelector('[id$="__custom_scene_wall"]');
      if (!sceneWall) return false;

      var rect = sceneWall.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight;
    }

    function onScroll() {
      if (_rafPending) return;
      _rafPending = true;
      requestAnimationFrame(function () {
        _rafPending = false;
        if (window.scrollY > window.innerHeight * 1.5 && !isSceneWallVisible()) {
          btn.classList.add('is-visible');
        } else {
          btn.classList.remove('is-visible');
        }
        syncStickyOffset();
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    document.addEventListener('shopify:section:load', onScroll);
    document.addEventListener('shopify:section:unload', onScroll);
    document.addEventListener('shopify:section:reorder', onScroll);

    var stickyCta = document.querySelector('[data-pdp-sticky]');
    if (stickyCta) {
      new MutationObserver(syncStickyOffset).observe(stickyCta, {
        attributes: true,
        attributeFilter: ['class']
      });
      if ('ResizeObserver' in window) {
        new ResizeObserver(syncStickyOffset).observe(stickyCta);
      }
    }

    onScroll();

    // Click: scroll to top, respecting prefers-reduced-motion
    btn.addEventListener('click', function () {
      var behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth';
      window.scrollTo({ top: 0, behavior: behavior });
    });
  }

  // ─────────────────────────────────────────────────────────────
  // initMailtoLinks — clipboard copy + toast fallback for mailto: links
  // Covers: wholesale / support / footer / contact / FAQ / 404 / 500
  //   inline mailto links site-wide — zero per-section wiring needed.
  // Behaviour:
  //   - Does NOT preventDefault: mailto: still opens mail client if one
  //     is configured (best-case path unchanged).
  //   - On every click: also copies the email address to clipboard
  //     and shows a toast "Copied — email@…" so the button is never
  //     silent when no mail client is present.
  // Double-init guard: data-mailto-init on each <a> element.
  // ─────────────────────────────────────────────────────────────
  function initMailtoLinks() {
    var links = document.querySelectorAll('a[href^="mailto:"]');
    if (!links.length) return;

    links.forEach(function (link) {
      if (link.dataset.mailtoInit) return;
      link.dataset.mailtoInit = '1';

      link.addEventListener('click', function () {
        // Parse email: strip "mailto:" prefix, drop any ?subject=… query
        var raw = link.getAttribute('href').replace(/^mailto:/i, '');
        var email = decodeURIComponent(raw.split('?')[0]);
        if (!email) return;

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(email).then(function () {
            showToast(email);
          }).catch(function () {
            fallbackMailtoCopy(email);
          });
        } else {
          fallbackMailtoCopy(email);
        }
      });
    });

    function fallbackMailtoCopy(email) {
      var ta = document.createElement('textarea');
      ta.value = email;
      ta.style.cssText = 'position:fixed;top:-9999px;left:-9999px';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) {}
      document.body.removeChild(ta);
      showToast(email);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // showToast — singleton toast used by initMailtoLinks (and any
  // future caller). Creates the .gh-toast element once, reuses it.
  // ─────────────────────────────────────────────────────────────
  var _toastEl = null;
  var _toastAddrEl = null;
  var _toastTimer = null;

  function showToast(email) {
    if (!_toastEl) {
      var accessibilityStrings = window.accessibilityStrings || {};

      _toastEl = document.createElement('div');
      _toastEl.className = 'gh-toast';

      var ck = document.createElement('span');
      ck.className = 'gh-toast__ck';
      ck.textContent = '✓';
      _toastEl.appendChild(ck);

      var body = document.createElement('span');
      body.className = 'gh-toast__body';

      var title = document.createElement('span');
      title.className = 'gh-toast__title';
      title.textContent = accessibilityStrings.emailAddressCopied || '';
      body.appendChild(title);

      _toastAddrEl = document.createElement('span');
      _toastAddrEl.className = 'gh-toast__addr';
      body.appendChild(_toastAddrEl);

      var hint = document.createElement('span');
      hint.className = 'gh-toast__hint';
      hint.textContent = accessibilityStrings.emailCopyHint || '';
      body.appendChild(hint);

      _toastEl.appendChild(body);

      _toastEl.addEventListener('click', function () {
        if (_toastTimer) { clearTimeout(_toastTimer); _toastTimer = null; }
        _toastEl.classList.remove('is-visible');
      });

      document.body.appendChild(_toastEl);
    }
    _toastAddrEl.textContent = email;
    if (_toastTimer) clearTimeout(_toastTimer);
    _toastEl.classList.add('is-visible');
    _toastTimer = setTimeout(function () {
      _toastEl.classList.remove('is-visible');
      _toastTimer = null;
    }, 4000);
  }

  // ─────────────────────────────────────────────────────────────
  // Announcement Bar (gh-anbar) — ⓘ info icon toggle
  //   - Click ⓘ button → toggle detail popover open/closed
  //   - Click outside popover or Esc → close
  //   - aria-expanded on button + aria-hidden + [hidden] on popover kept in sync
  //   - Idempotent via data-anbar-init guard
  // ─────────────────────────────────────────────────────────────
  function initAnnounceBar(root) {
    root = root || document;
    var bars = root.querySelectorAll('.gh-anbar:not([data-anbar-init])');
    if (!bars.length) return;

    bars.forEach(function (bar) {
      bar.setAttribute('data-anbar-init', '');

      var btn    = bar.querySelector('.gh-anbar__info-btn');
      var detail = bar.querySelector('.gh-anbar__detail');
      if (!btn || !detail) return;

      function isOpen() {
        return btn.getAttribute('aria-expanded') === 'true';
      }

      function openDetail() {
        detail.removeAttribute('hidden');
        detail.setAttribute('aria-hidden', 'false');
        btn.setAttribute('aria-expanded', 'true');
      }

      function closeDetail() {
        detail.setAttribute('hidden', '');
        detail.setAttribute('aria-hidden', 'true');
        btn.setAttribute('aria-expanded', 'false');
      }

      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        if (isOpen()) {
          closeDetail();
        } else {
          openDetail();
        }
      });

      // Close on outside click
      document.addEventListener('click', function (e) {
        if (!isOpen()) return;
        if (!bar.contains(e.target)) {
          closeDetail();
        }
      });

      // Close on Esc
      document.addEventListener('keydown', function (e) {
        if (!isOpen()) return;
        if (e.key === 'Escape' || e.key === 'Esc') {
          closeDetail();
          btn.focus();
        }
      });
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Collection sorting — preserve the current locale, unknown query
  // parameters, click IDs, and repeated values; only replace sort_by.
  // ─────────────────────────────────────────────────────────────
  function buildCollectionSortUrl(currentHref, sortValue) {
    var url = new URL(currentHref, window.location.origin);
    url.searchParams.set('sort_by', sortValue);
    return url.pathname + url.search + url.hash;
  }

  function initCollectionSort(root) {
    root = root || document;
    root.querySelectorAll('[data-collection-sort]:not([data-collection-sort-init])').forEach(function (select) {
      select.setAttribute('data-collection-sort-init', '');
      select.addEventListener('change', function () {
        window.location.assign(buildCollectionSortUrl(window.location.href, select.value));
      });
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Collection Add-to-Cart (gh-shop-card__cta)
  //   - Uses Shopify's locale-aware routes and the active notification/drawer.
  //   - Sends exactly one add mutation. Rejected or uncertain responses trigger
  //     a read-only cart refresh, never an automatic retry.
  //   - Distinguishes a successful mutation from a later UI-render failure.
  // ─────────────────────────────────────────────────────────────
  function initCollectionATC(root) {
    root = root || document;
    if (root._catcInit) return;
    root._catcInit = true;

    root.addEventListener('click', function (e) {
      var btn = e.target.closest('.gh-shop-card__cta[data-variant-id]');
      if (!btn) return;

      var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
      if (!variantId) return;

      e.preventDefault();

      btn.disabled = true;
      var originalText = btn.textContent;
      btn.textContent = '...';
      var card = btn.closest('.gh-shop-card');
      var errorEl = card && card.querySelector('[data-collection-add-error]');
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }
      var cartUi = document.querySelector('cart-notification, cart-drawer');
      var formData = new FormData();
      formData.append('id', String(variantId));
      formData.append('quantity', '1');
      if (cartUi && typeof cartUi.getSectionsToRender === 'function') {
        formData.append('sections', cartUi.getSectionsToRender().map(function (section) {
          return section.id;
        }).join(','));
        formData.append('sections_url', window.location.pathname);
      }
      if (cartUi && typeof cartUi.setActiveElement === 'function') {
        cartUi.setActiveElement(btn);
      }
      var mutationSucceeded = false;
      var fallbackNavigationPending = false;

      fetch(getThemeRoute('cart_add_url', 'cart/add.js'), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      })
      .then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (payload) {
          if (!res.ok || payload.status) {
            var serverError = new Error(getCartErrorMessage(payload));
            serverError.serverRejected = true;
            throw serverError;
          }
          return payload;
        });
      })
      .then(function (payload) {
        mutationSucceeded = true;
        if (!cartUi || typeof cartUi.renderContents !== 'function' || !payload.sections) {
          fallbackNavigationPending = true;
          window.location.assign(getThemeRoute('cart_url', 'cart'));
          return;
        }
        try {
          cartUi.renderContents(payload);
        } catch (uiError) {
          fallbackNavigationPending = true;
          window.location.assign(getThemeRoute('cart_url', 'cart'));
          return;
        }

        return fetchCartState()
          .then(updateCartBadges)
          .catch(function () {
            // The mutation succeeded, but the visible badge cannot be reconciled.
            // Use the cart page as a read-only truth fallback; never retry the add.
            fallbackNavigationPending = true;
            window.location.assign(getThemeRoute('cart_url', 'cart'));
          });
      })
      .catch(function (err) {
        if (mutationSucceeded) return;
        return refreshCartTruth(cartUi, variantId)
          .catch(function () {
            // Preserve the original rejection message even if read-back fails.
          })
          .then(function () {
            if (errorEl) {
              errorEl.textContent = getErrorFallback(errorEl, 'data-add-fallback-message', err);
              errorEl.hidden = false;
            }
          });
      })
      .finally(function () {
        if (fallbackNavigationPending) return;
        btn.textContent = originalText;
        btn.disabled = false;
      });
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Commerce click tracking
  //   - Uses one document-level delegated listener.
  //   - Sends two click events through the existing GA4 tag without blocking navigation.
  //   - Excludes collection Quick add and other non-product links.
  // ─────────────────────────────────────────────────────────────
  // Preserve the verified privacy purposes of legacy GA4 pixel155058402.
  var COMMERCE_CTA_MEASUREMENT_ID = 'G-06SGWGPMZZ';
  var COMMERCE_CTA_MAIN_THEME_ID = '159308611810';
  var COMMERCE_CTA_PRIVACY_METHODS = ['analyticsProcessingAllowed', 'marketingAllowed', 'saleOfDataAllowed'];

  function isCommerceClickPrivacyAllowed() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy) return false;
    try {
      return COMMERCE_CTA_PRIVACY_METHODS.every(function (method) {
        return typeof privacy[method] === 'function' && privacy[method]() === true;
      });
    } catch (error) {
      return false;
    }
  }

  function publishCommerceEvent(name, data) {
    if (name !== 'blog_cta_click' && name !== 'collection_product_click') return;
    var theme = window.Shopify && window.Shopify.theme;
    if (!theme || String(theme.id) !== COMMERCE_CTA_MAIN_THEME_ID || String(theme.role).toLowerCase() !== 'main') return;
    if (!isCommerceClickPrivacyAllowed() || typeof window.gtag !== 'function') return;
    try {
      // Reuse the existing tag; do not initialize, configure, retry or wait.
      data.send_to = COMMERCE_CTA_MEASUREMENT_ID;
      data.transport_type = 'beacon';
      window.gtag('event', name, data);
    } catch (error) {
      // Analytics must never block the visitor's original click action.
    }
  }

  function getPathHandle(pathname, resource, offset) {
    var parts = (pathname || '').split('/').filter(Boolean);
    var resourceIndex = parts.indexOf(resource);
    if (resourceIndex === -1) return '';

    var value = parts[resourceIndex + offset] || '';
    try {
      return decodeURIComponent(value);
    } catch (error) {
      return value;
    }
  }

  function initCommerceClickTracking() {
    if (document._ghCommerceClickTrackingInit) return;
    document._ghCommerceClickTrackingInit = true;

    document.addEventListener('click', function (event) {
      if (!event.target || typeof event.target.closest !== 'function') return;

      // Quick add is a cart action, not a product-card navigation click.
      if (event.target.closest('.gh-shop-card__cta[data-variant-id], .gh-product-card__cta')) return;

      var link = event.target.closest('a');
      if (!link) return;

      var destination;
      try {
        destination = new URL(link.href, window.location.href);
      } catch (error) {
        return;
      }

      if (!/^https?:$/.test(destination.protocol) || destination.origin !== window.location.origin) return;
      // Strip query/hash from measurement only; preserve the real link target.
      var destinationUrl = destination.origin + destination.pathname;

      if (link.matches('.gh-article__cta-btn')) {
        publishCommerceEvent('blog_cta_click', {
          source_path: window.location.pathname,
          destination_url: destinationUrl,
          article_handle: getPathHandle(window.location.pathname, 'blogs', 2),
          product_handle: getPathHandle(destination.pathname, 'products', 1),
          placement: 'bottom_cta'
        });
        return;
      }

      var card = link.closest('.gh-product-card');
      if (!card || !getPathHandle(destination.pathname, 'products', 1)) return;

      var linkArea = '';
      if (link.matches('.gh-product-card__link')) {
        linkArea = 'image';
      } else if (link.closest('.gh-product-card__name')) {
        linkArea = 'title';
      } else {
        return;
      }

      var cards = Array.prototype.slice.call(document.querySelectorAll('.gh-product-card'));
      var cardPosition = cards.indexOf(card) + 1;

      publishCommerceEvent('collection_product_click', {
        source_path: window.location.pathname,
        destination_url: destinationUrl,
        collection_handle: getPathHandle(window.location.pathname, 'collections', 1),
        product_handle: card.getAttribute('data-product') || getPathHandle(destination.pathname, 'products', 1),
        link_area: linkArea,
        position: cardPosition
      });
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Article TOC — builds a <details>-based jump-nav above content
  // Only renders when the article page has ≥ 3 H2 headings.
  // ─────────────────────────────────────────────────────────────
  function initArticleToc() {
    if (!document.querySelector('.gh-article__content')) return;

    var content = document.querySelector('.gh-article__content');
    var heads = Array.prototype.slice.call(content.querySelectorAll('h2'));
    if (heads.length < 3) return;

    // Guard: don't insert twice (e.g. after shopify:section:load)
    if (document.querySelector('.gh-article__toc')) return;

    // Build language-aware label
    var lang = ((document.documentElement.lang || 'en').slice(0, 2)).toLowerCase();
    var labels = {
      en: 'In this article',
      de: 'In diesem Artikel',
      es: 'En este artículo',
      fr: 'Dans cet article',
      zh: '文章目录'
    };
    var label = labels[lang] || labels['en'];

    // Slug helper: text → url-safe id
    var usedIds = {};
    function toSlug(text) {
      var raw = text
        .toLowerCase()
        .replace(/&[a-z]+;/g, '')       // strip HTML entities
        .replace(/[^a-z0-9À-ɏ]+/g, '-')  // non-alnum → hyphen
        .replace(/^-+|-+$/g, '');       // trim leading/trailing hyphens
      if (!raw) raw = 'section';
      var base = raw;
      var n = 1;
      while (usedIds[raw]) {
        n++;
        raw = base + '-' + n;
      }
      usedIds[raw] = true;
      return raw;
    }

    // Assign ids to every h2
    heads.forEach(function (h) {
      if (!h.id) {
        h.id = toSlug(h.textContent || '');
      } else {
        // Record existing id so de-dup counters stay accurate
        usedIds[h.id] = true;
      }
    });

    // Build the <details> element using DOM APIs (no innerHTML on user text)
    var toc = document.createElement('details');
    toc.className = 'gh-article__toc';

    var summary = document.createElement('summary');
    summary.textContent = label;
    toc.appendChild(summary);

    var ol = document.createElement('ol');
    heads.forEach(function (h) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = h.textContent || '';
      // Smooth scroll + hash update
      a.addEventListener('click', function (e) {
        e.preventDefault();
        var target = document.getElementById(h.id);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth' });
          try { history.pushState(null, '', '#' + h.id); } catch (err) { /* ignore */ }
        }
      });
      li.appendChild(a);
      ol.appendChild(li);
    });
    toc.appendChild(ol);

    // Desktop: open by default
    if (window.matchMedia('(min-width:1200px)').matches) {
      toc.open = true;
    }

    // Insert: after .gh-article__meta if present, otherwise before content
    var meta = document.querySelector('.gh-article__meta');
    if (meta && meta.parentNode === content.parentNode) {
      meta.parentNode.insertBefore(toc, meta.nextSibling);
    } else {
      content.parentNode.insertBefore(toc, content);
    }
  }

})();

/* Shopify Inbox activator — approved 2026-10-06 storefront styling. */
(function initShopifyInboxActivator() {
  const styleId = 'gh-shopify-inbox-activator-style';
  const maxAttempts = 40;
  const observedRoots = new WeakSet();
  let attempts = 0;
  let scanQueued = false;
  let inboxViewportBound = false;
  let inboxViewportFrame = 0;
  const sideChatPageSelector = '[data-pdp-sticky-top], .gh-hero';

  function syncInboxViewport() {
    inboxViewportFrame = 0;
    const host = document.querySelector('shopify-chat');
    if (!host || !document.querySelector(sideChatPageSelector)) return;
    const viewport = window.visualViewport;
    const width = viewport ? viewport.width : document.documentElement.clientWidth;
    const height = viewport ? viewport.height : document.documentElement.clientHeight;
    const left = viewport ? viewport.offsetLeft : 0;
    const top = viewport ? viewport.offsetTop : 0;
    const box = host.getBoundingClientRect();
    // A hidden UGC/modal host has no usable geometry; retain its last insets.
    if (box.width <= 0 || box.height <= 0) return;
    host.style.setProperty('--gh-inbox-viewport-width', width + 'px');
    host.style.setProperty('--gh-inbox-viewport-height', height + 'px');
    host.style.setProperty('--gh-inbox-overflow-right', Math.max(0, box.right - left - width) + 'px');
    host.style.setProperty('--gh-inbox-overflow-bottom', Math.max(0, box.bottom - top - height) + 'px');
  }

  function queueInboxViewport() {
    if (!inboxViewportFrame) inboxViewportFrame = window.requestAnimationFrame(syncInboxViewport);
  }

  function bindInboxViewport() {
    if (inboxViewportBound || !document.querySelector(sideChatPageSelector)) return;
    inboxViewportBound = true;
    new MutationObserver(queueInboxViewport).observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
    window.addEventListener('resize', queueInboxViewport, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', queueInboxViewport, { passive: true });
      window.visualViewport.addEventListener('scroll', queueInboxViewport, { passive: true });
    }
  }

  function collectRoots() {
    const host = document.querySelector('shopify-chat');
    const roots = host && host.shadowRoot ? [host.shadowRoot] : [];
    const discovered = [];
    for (let index = 0; index < roots.length; index += 1) {
      const root = roots[index];
      discovered.push(root);
      const elements = root.querySelectorAll('*');
      for (const element of elements) {
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
    return discovered;
  }

  function queueStyleScan() {
    if (scanQueued) return;
    scanQueued = true;
    window.requestAnimationFrame(function scanShopifyInboxRoots() {
      scanQueued = false;
      installStyle();
    });
  }

  function observeRoot(root) {
    if (observedRoots.has(root)) return;
    observedRoots.add(root);
    new MutationObserver(queueStyleScan).observe(root, { childList: true, subtree: true });
  }

  function installStyle() {
    const roots = collectRoots();
    if (!roots.length) return false;
    bindInboxViewport();
    syncInboxViewport();
    let styledRuntime = false;
    for (const root of roots) {
      observeRoot(root);
      const rootHost = root.host;
      const isChatRoot = rootHost && rootHost.tagName === 'SHOPIFY-CHAT';
      const isRuntimeRoot = rootHost && rootHost.tagName === 'SHOPIFY-AGENT-RUNTIME';
      if (!isChatRoot && !isRuntimeRoot) continue;
      if (isRuntimeRoot) styledRuntime = true;
      if (root.getElementById(styleId)) continue;

      const style = document.createElement('style');
      style.id = styleId;
      const isSideChatPage = !!document.querySelector(sideChatPageSelector);
      style.textContent = `
      .activator {
        box-sizing: border-box !important;
        border: 3px solid rgba(255, 255, 255, .98) !important;
        border-radius: 999px !important;
        background: linear-gradient(145deg, #2A9AA5 0%, #1A7075 100%) !important;
        color: #fff !important;
        box-shadow: 0 10px 26px rgba(26, 112, 117, .30), 0 2px 8px rgba(15, 25, 35, .16) !important;
      }
      .activator:hover {
        filter: brightness(1.06);
      }
      .activator:focus-visible {
        outline: 3px solid #C8E8EA !important;
        outline-offset: 3px !important;
      }
      .activator__icon,
      .activator__icon shopify-chat-icon {
        color: #fff !important;
      }
      ${isSideChatPage ? `
      @media (max-width: 1023px) {
        .activator-nudge:has(> .activator[aria-expanded="false"]) {
          right: auto !important;
          left: min(calc(100% - 32px), calc(100vw - 32px)) !important;
          bottom: auto !important;
          top: 50vh !important;
          top: 50dvh !important;
          transform: translateY(-50%) !important;
        }
        .activator[aria-expanded="false"] {
          position: relative !important;
          width: 32px !important;
          min-width: 32px !important;
          max-width: 32px !important;
          height: 76px !important;
          min-height: 76px !important;
          padding: 0 !important;
          border: 0 !important;
          border-radius: 0 !important;
          background: #208D96 !important;
          box-shadow: 0 3px 12px rgba(15,25,35,.16) !important;
        }
        .activator[aria-expanded="false"] .activator__icon,
        .activator[aria-expanded="false"] .activator__label {
          display: none !important;
        }
        .activator[aria-expanded="false"]:focus-visible {
          outline: 2px solid #fff !important;
          outline-offset: -4px !important;
        }
        .activator[aria-expanded="false"]::after {
          content: 'CHAT';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(-90deg);
          color: #fff;
          font: 700 11px/1 Arial, sans-serif;
          letter-spacing: .16em;
          white-space: nowrap;
          pointer-events: none;
        }
      }
      @media (max-width: 767px) {
        .panel.panel {
          right: calc(12px + var(--gh-inbox-overflow-right, 0px)) !important;
          bottom: calc(12px + var(--gh-inbox-overflow-bottom, 0px)) !important;
          left: auto !important;
          top: auto !important;
          margin: 0 !important;
          max-width: min(366px, calc(var(--gh-inbox-viewport-width, 100vw) - 24px)) !important;
          max-height: calc(var(--gh-inbox-viewport-height, 100dvh) - 24px) !important;
        }
      }` : ''}
      @media (max-width: 767px) {
        .panel {
          width: calc(100vw - 24px) !important;
          max-width: 366px !important;
          height: min(350px, calc(100vh - 104px)) !important;
          min-height: 0 !important;
          right: 12px !important;
          bottom: 12px !important;
          border-radius: 24px !important;
        }
        .panel iframe {
          width: 100% !important;
          height: 100% !important;
        }
      }
    `;
      root.appendChild(style);
    }
    return styledRuntime;
  }

  function waitForActivator() {
    if (installStyle()) return;
    const timer = window.setInterval(function waitForShopifyInbox() {
      attempts += 1;
      if (installStyle() || attempts >= maxAttempts) window.clearInterval(timer);
    }, 250);
    window.addEventListener('pagehide', function stopInboxStyleTimer() {
      window.clearInterval(timer);
    }, { once: true });
  }

  if (window.customElements && window.customElements.whenDefined) {
    window.customElements.whenDefined('shopify-chat').then(waitForActivator);
  } else {
    waitForActivator();
  }
})();
